import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Chip, Button, TextField, CircularProgress } from "@mui/material";
import { tableFromIPC } from "apache-arrow";
import MainPage from "../MainPage";
import { getAIConfig, getAIColours } from "../utils/ai_aggregators";
import { getPolConfig, getpolColours } from "../utils/polAggregators";
import { buildLabelsAndColors } from "../utils/colorScale";
import { stateFipsToName } from "../utils/idUtils";

// ─── Real data fetch ──────────────────────────────────────────────────────────
const PM_KEYS = [
  "prediction_markets__banned_pred_n",
  "prediction_markets__same_gambling_pred_n",
  "prediction_markets__diff_gambling_pred_n",
  "prediction_markets__no_regulation_pred_n",
  "prediction_markets__dk_pred_n",
];

async function _arrowToRows(res) {
  const buf = await res.arrayBuffer();
  const table = tableFromIPC(buf);
  const rows = [];
  for (let i = 0; i < table.numRows; i++) {
    const row = {};
    for (const field of table.schema.fields) {
      row[field.name] = table.getChild(field.name).get(i);
    }
    rows.push(row);
  }
  return rows;
}


async function fetchTexasCds() {
  const res = await fetch("/api/cd_data_arrow/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ theme: "gamble", groupKeys: PM_KEYS, stateCodes: ["48"], schema: "single" }),
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return _arrowToRows(res);
}

async function fetchCalCds() {
  const res = await fetch("/api/cd_data_arrow/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ theme: "gamble", groupKeys: PM_KEYS, stateCodes: ["06"], schema: "single" }),
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return _arrowToRows(res);
}

async function fetchNationalPm() {
  // Use states endpoint without filter — average across all states weighted by total
  const res = await fetch("/api/states_data_arrow/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ theme: "gamble", groupKeys: PM_KEYS, schema: "single" }),
  });
  if (!res.ok) throw new Error(`${res.status}`);
  const rows = await _arrowToRows(res);
  if (!rows.length) return null;

  // Weighted average across all state rows
  let totalW = 0;
  const sums = { ban: 0, same: 0, diff: 0, none: 0, dk: 0 };
  for (const r of rows) {
    const w = Number(r.total ?? 1);
    totalW += w;
    sums.ban  += (r["prediction_markets__banned_pred_n"]        ?? 0) * w;
    sums.same += (r["prediction_markets__same_gambling_pred_n"] ?? 0) * w;
    sums.diff += (r["prediction_markets__diff_gambling_pred_n"] ?? 0) * w;
    sums.none += (r["prediction_markets__no_regulation_pred_n"] ?? 0) * w;
    sums.dk   += (r["prediction_markets__dk_pred_n"]            ?? 0) * w;
  }
  if (!totalW) return null;
  return {
    ban:  Math.round(sums.ban  / totalW),
    same: Math.round(sums.same / totalW),  // ~33%
    diff: Math.round(sums.diff / totalW),  // ~18%
    none: Math.round(sums.none / totalW),  // ~9%
    dk:   Math.round(sums.dk   / totalW),  // ~26%
  };
}

function rowToBreakdown(row) {
  if (!row) return null;
  const r = (k) => Math.round((row[k] ?? 0) * 100) / 100;
  return {
    ban:  r("prediction_markets__banned_pred_n"),
    same: r("prediction_markets__same_gambling_pred_n"),
    diff: r("prediction_markets__diff_gambling_pred_n"),
    none: r("prediction_markets__no_regulation_pred_n"),
    dk:   r("prediction_markets__dk_pred_n"),
  };
}

// Find a row by area_id, trying both raw and padded formats
function findRow(rows, areaId) {
  const pad5 = String(areaId).padStart(5, "0");
  return rows.find(r => {
    const id = String(r.area_id ?? "");
    return id === String(areaId) || id === pad5;
  }) || null;
}

const CA_FIPS = "06";

// ─── Data constants ───────────────────────────────────────────────────────────
// National approximate averages — illustrative; verify against MRP output
// Bar order: ban | same-as-gambling | not-sure | different | no-reg
// Visual logic: red=against, orange=incumbent position, grey=undecided, teal=open to change
const PM_OPTS = [
  { key: "prediction_markets__banned_pred_n",        label: "Ban entirely",            color: "#C1121F", pct: 15 },
  { key: "prediction_markets__same_gambling_pred_n", label: "Same as gambling",        color: "#F77F00", pct: 33 },
  { key: "prediction_markets__dk_pred_n",            label: "Not sure",                color: "#94a3b8", pct: 26 },
  { key: "prediction_markets__diff_gambling_pred_n", label: "Different from gambling", color: "#4CC9F0", pct: 18 },
  { key: "prediction_markets__no_regulation_pred_n", label: "No regulation",           color: "#06D6A0", pct: 8 },
];

// California CD breakdown — illustrative fallback; real data loaded from API
const CA_CDS = [
  { name: "CA-16", region: "Silicon Valley",        ban: 8,  same: 22, diff: 27, none: 16, dk: 27 },
  { name: "CA-17", region: "San Jose / South Bay",  ban: 9,  same: 24, diff: 25, none: 14, dk: 28 },
  { name: "CA-45", region: "Orange County",         ban: 10, same: 28, diff: 20, none: 10, dk: 32 },
  { name: "CA-47", region: "OC coast / Irvine",     ban: 11, same: 27, diff: 19, none: 11, dk: 32 },
  { name: "CA-21", region: "Central Valley",        ban: 7,  same: 20, diff: 16, none: 22, dk: 35 },
  { name: "CA-22", region: "Tulare / Kings County", ban: 8,  same: 22, diff: 15, none: 20, dk: 35 },
];

// Approximate geographic centers for zoom-to-district
const TX_CD_CENTERS = {
  "4801": [-94.5, 31.5],  "4802": [-95.2, 30.2],  "4803": [-96.7, 33.1],
  "4804": [-94.8, 32.5],  "4805": [-95.8, 32.3],  "4806": [-97.1, 32.7],
  "4807": [-95.5, 29.7],  "4808": [-95.3, 30.2],  "4809": [-95.3, 29.6],
  "4810": [-96.8, 29.9],  "4811": [-102.5, 31.8], "4812": [-97.4, 32.8],
  "4813": [-101.8, 35.5], "4814": [-94.1, 30.1],  "4815": [-98.2, 26.8],
  "4816": [-106.5, 31.8], "4817": [-97.1, 31.2],  "4818": [-95.3, 29.8],
  "4819": [-101.8, 33.5], "4820": [-98.5, 29.3],  "4821": [-99.3, 30.2],
  "4822": [-95.5, 29.5],  "4823": [-101.0, 29.5], "4824": [-96.9, 32.8],
  "4825": [-97.8, 30.4],  "4826": [-97.1, 33.1],  "4827": [-97.5, 28.3],
  "4828": [-99.5, 27.8],  "4829": [-95.1, 29.7],  "4830": [-96.8, 32.8],
  "4831": [-97.7, 30.6],  "4832": [-96.8, 32.9],  "4833": [-97.3, 32.7],
  "4834": [-97.4, 25.9],  "4835": [-98.1, 29.7],  "4836": [-94.9, 29.8],
  "4837": [-97.7, 30.3],  "4838": [-95.7, 29.8],
};

function txTopCenter(areaIds) {
  const pts = areaIds.map(id => TX_CD_CENTERS[id]).filter(Boolean);
  if (!pts.length) return [-96.5, 30.5];
  return [
    pts.reduce((s, p) => s + p[0], 0) / pts.length,
    pts.reduce((s, p) => s + p[1], 0) / pts.length,
  ];
}

// Texas CD config — all 38 districts, data drives the story
const TX_CD_CONFIG = [
  { name: "TX-1",  areaId: "4801", region: "East Texas" },
  { name: "TX-2",  areaId: "4802", region: "North Houston suburbs" },
  { name: "TX-3",  areaId: "4803", region: "Dallas suburbs / Plano" },
  { name: "TX-4",  areaId: "4804", region: "East Texas / Tyler" },
  { name: "TX-5",  areaId: "4805", region: "East Texas / Athens" },
  { name: "TX-6",  areaId: "4806", region: "DFW suburbs / Arlington" },
  { name: "TX-7",  areaId: "4807", region: "Houston inner suburbs" },
  { name: "TX-8",  areaId: "4808", region: "Houston exurbs / Woodlands" },
  { name: "TX-9",  areaId: "4809", region: "South Houston" },
  { name: "TX-10", areaId: "4810", region: "Houston-Austin corridor" },
  { name: "TX-11", areaId: "4811", region: "Midland / Odessa" },
  { name: "TX-12", areaId: "4812", region: "Fort Worth suburbs" },
  { name: "TX-13", areaId: "4813", region: "Panhandle / Amarillo" },
  { name: "TX-14", areaId: "4814", region: "Beaumont / Port Arthur" },
  { name: "TX-15", areaId: "4815", region: "Rio Grande Valley north" },
  { name: "TX-16", areaId: "4816", region: "El Paso" },
  { name: "TX-17", areaId: "4817", region: "Waco / Brazos Valley" },
  { name: "TX-18", areaId: "4818", region: "Houston urban" },
  { name: "TX-19", areaId: "4819", region: "Lubbock / West Texas" },
  { name: "TX-20", areaId: "4820", region: "San Antonio south" },
  { name: "TX-21", areaId: "4821", region: "Austin / Hill Country" },
  { name: "TX-22", areaId: "4822", region: "Houston outer suburbs" },
  { name: "TX-23", areaId: "4823", region: "San Antonio to El Paso border" },
  { name: "TX-24", areaId: "4824", region: "Dallas suburbs / Irving" },
  { name: "TX-25", areaId: "4825", region: "Austin exurbs" },
  { name: "TX-26", areaId: "4826", region: "DFW suburbs / Flower Mound" },
  { name: "TX-27", areaId: "4827", region: "Corpus Christi / Victoria" },
  { name: "TX-28", areaId: "4828", region: "Laredo / border" },
  { name: "TX-29", areaId: "4829", region: "Houston east / Pasadena" },
  { name: "TX-30", areaId: "4830", region: "Dallas urban" },
  { name: "TX-31", areaId: "4831", region: "Austin suburbs / Round Rock" },
  { name: "TX-32", areaId: "4832", region: "Dallas suburban" },
  { name: "TX-33", areaId: "4833", region: "Fort Worth urban" },
  { name: "TX-34", areaId: "4834", region: "Rio Grande Valley / Brownsville" },
  { name: "TX-35", areaId: "4835", region: "Austin-San Antonio corridor" },
  { name: "TX-36", areaId: "4836", region: "Houston east suburbs" },
  { name: "TX-37", areaId: "4837", region: "Austin urban" },
  { name: "TX-38", areaId: "4838", region: "Houston west / Katy" },
];

// Demographic group toggles — gender/age/vote24 filter values match DEMO_LABELS
const DEMO_GROUPS = [
  { label: "All adults",    desc: "National baseline",                     gender: null, age: null, vote24: null, better: 27, worse: 39, aidk: 11 },
  { label: "Men 18–34",     desc: "Most optimistic group",                 gender: 1,    age: 1,    vote24: null, better: 42, worse: 22, aidk: 8  },
  { label: "Women 55+",     desc: "Most pessimistic group",                gender: 2,    age: 3,    vote24: null, better: 16, worse: 52, aidk: 14 },
];

// ─── Step definitions ─────────────────────────────────────────────────────────
const STEPS = [
  {
    name: "intro",
    headline: "Turning research into practical tools for decision-making.",
    copy: "District Profiles turns a single national survey into interactive, local-level insights across congressional districts, states, counties, state legislative districts, and even down to ZIP code level.",
    methodology: true,
    insight: "Tool intro, methodology, and sample",
    mapLabel: "Economic approval: net score by congressional district",
    mapView: { variable: "econ_approve", geoLevel: "cd" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 0 },
  },
  {
    name: "explore",
    headline: "Immigration",
    copy: "",
    insight: "Net approval -3 and falling fast in swing seats",
    mapLabel: "Immigration policy approval: net score by congressional district",
    mapView: { variable: "immig_attitudes", geoLevel: "cd" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
    mapHoverHint: true,
  },
  {
    name: "light-touch",
    headline: "Filter by demographic group",
    copy: "",
    insight: "A 30-point gap between youngest and oldest voters",
    mapLabel: "AI impact on society: net score by state",
    mapView: { variable: "ai_impact_society", geoLevel: "states", preferenceFilter: "net" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
    showDemoToggle: true,
    showGeoToggle: true,
  },
  {
    name: "texas-cd",
    headline: "Target messaging at district level",
    copy: "",
    mapHoverHint: false,
    insight: "38 districts, different messages for each",
    mapLabel: "Prediction market regulation, Texas congressional districts",
    mapView: { variable: "prediction_markets_regulation", geoLevel: "states" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 800 },
    flyTo2: { center: [-99.0, 31.5], zoom: 5.0, duration: 1400 },
    flyTo2Delay: 1000,
    flyTo2GeoLevel: "cd",
    flyTo2MapView: { variable: "prediction_markets_regulation", geoLevel: "cd", stateFilter: "48" },
  },
  {
    name: "use-cases",
    headline: "Turn insights into action",
    insight: "Strategy, comms, and legislative intelligence",
    mapLabel: "Economic approval: net score by congressional district",
    mapView: { variable: "econ_approve", geoLevel: "cd" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
  },
  {
    name: "cta",
    headline: "Ready to explore?",
    copy: "",
    insight: "Full access, all geographies",
    isCta: true,
    mapLabel: "Economic approval: net score by congressional district",
    mapView: { variable: "econ_approve", geoLevel: "cd" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1400 },
  },
];

// ─── DemoSignupOverlay ────────────────────────────────────────────────────────
function DemoSignupOverlay({ embedded = false }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/user/demo-signup/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong");
      setSuccess(data?.verify_url ? `Dev link: ${data.verify_url}` : true);
    } catch (err) {
      setError(err.message || "Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <Box sx={{ width: embedded ? "100%" : "min(420px, 90%)", bgcolor: "#fff", borderRadius: embedded ? "0" : "16px", p: embedded ? 0 : 3.5, boxShadow: embedded ? "none" : "0 24px 60px rgba(0,0,0,0.25)" }}>
      {success ? (
        <>
          <Typography sx={{ fontSize: "22px", fontWeight: 800, color: INK, fontFamily: F, mb: 1 }}>Check your email</Typography>
          <Typography sx={{ fontSize: "14px", color: MUTED, fontFamily: F, lineHeight: 1.6 }}>
            {typeof success === "string" ? success : "Demo account created. Check your email to verify and activate access."}
          </Typography>
        </>
      ) : (
        <>
          <Typography sx={{ fontSize: "22px", fontWeight: 800, color: INK, fontFamily: F, mb: 0.5 }}>Get demo access</Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <TextField size="small" label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: F } }} />
            <TextField size="small" label="Email" type="email" required value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: F } }} />
            <TextField size="small" label="Password" type="password" required value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: F } }} />
            {error && <Typography sx={{ fontSize: "12px", color: "#e11d48", fontFamily: F }}>{error}</Typography>}
            <Button type="submit" variant="contained" disableElevation disabled={submitting || !email || !password}
              sx={{ bgcolor: TEAL, color: "#fff", fontFamily: F, fontWeight: 700, fontSize: "14px", textTransform: "none", borderRadius: "10px", py: 1.1, boxShadow: `0 4px 20px ${TEAL_GLOW}`, "&:hover": { bgcolor: TEAL_DARK }, "&:disabled": { bgcolor: `${TEAL}55`, color: "#fff" } }}>
              {submitting ? "Creating account..." : "Get demo access"}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );

  if (embedded) return content;
  return (
    <Box sx={{ position: "absolute", inset: 0, zIndex: 20, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(15,23,42,0.55)", backdropFilter: "blur(6px)", animation: "fadeInOverlay 0.5s ease both", "@keyframes fadeInOverlay": { from: { opacity: 0 }, to: { opacity: 1 } } }}>
      {content}
    </Box>
  );
}

// ─── AppPanelWrapper ──────────────────────────────────────────────────────────
const AppPanelWrapper = React.forwardRef(function AppPanelWrapper({ interactive = false }, ref) {
  const noopNavigate = useCallback(() => {}, []);
  return (
    <MainPage
      ref={ref}
      slug="gamble"
      navigate={noopNavigate}
      hideChrome
      skipAuthRedirect
      // FIX (c): interaction is OFF inline (touch-scroll passes through to the story)
      // and ON only when the map is expanded fullscreen on mobile (map fills the
      // screen, no story to scroll past). Desktop never sets interactive → unchanged.
      disableMapInteraction={!interactive}
      // ISSUE A: mobile preloads only "cd" (the first + most steps' level) to halve the
      // up-front FlatGeobuf fetch/parse. "media_market" is toggle-only (geo toggle on the
      // light-touch step) → deferred to lazy-load on demand, same as "states" already does.
      // Desktop keeps both preloaded (fast enough; unchanged).
      geoPreloadOrder={window.innerWidth < 600 ? ["cd"] : ["cd", "media_market"]}
    />
  );
});

// ─── Navigation dispatcher ────────────────────────────────────────────────────
let _cameraTimer = null;
let _flyTo2Timer = null;
let _lastFlyKey = "";

function getGlMap(mainInst) {
  return mainInst?._mapPageInstance?.mapRef?.current?.getMap?.() || null;
}

// FIX (e): reel exit (branding X + end-of-reel CTA). MOBILE (< 600) → the /m app,
// landing on the MAP (persist "map" so MobileApp doesn't restore the highlights
// picker on remount). DESKTOP → "/", unchanged (IntroPage's existing destination).
// Shared by the two call sites (branding X here + "Proceed" CTA in NarrativeCard).
function exitHighlights(navigate) {
  if (window.innerWidth < 600) {
    try { sessionStorage.setItem("m.activeScreen", "map"); } catch (e) { /* ignore */ }
    navigate("/m");
  } else {
    navigate("/");
  }
}

function armCrossfade(glMap, duration = 550) {
  if (!glMap) return;
  let style;
  try { style = glMap.getStyle(); } catch { return; }
  if (!style?.layers) return;
  const t = { duration, delay: 0 };
  style.layers.forEach((layer) => {
    try {
      if (layer.type === "fill") {
        glMap.setPaintProperty(layer.id, "fill-color-transition", t);
        glMap.setPaintProperty(layer.id, "fill-opacity-transition", t);
        glMap.setPaintProperty(layer.id, "fill-outline-color-transition", t);
      } else if (layer.type === "line") {
        glMap.setPaintProperty(layer.id, "line-color-transition", t);
        glMap.setPaintProperty(layer.id, "line-opacity-transition", t);
      }
    } catch { /* safe */ }
  });
}

function navigateToStep(mainInst, stepName, extraMapState = {}) {
  if (!mainInst) return;
  const step = STEPS.find((s) => s.name === stepName);
  if (!step) return;

  if (_cameraTimer) { clearTimeout(_cameraTimer); _cameraTimer = null; }
  if (_flyTo2Timer) { clearTimeout(_flyTo2Timer); _flyTo2Timer = null; }

  mainInst.setState({ chatHighlightAreas: step.highlights || [] });

  const glMap = getGlMap(mainInst);
  armCrossfade(glMap);

  if (step.mapView) {
    mainInst.setHighlightsState({ ...step.mapView, ...extraMapState });
  }

  if (step.flyTo) {
    const key = JSON.stringify(step.flyTo);
    if (key !== _lastFlyKey) {
      _lastFlyKey = key;
      _cameraTimer = setTimeout(() => {
        _cameraTimer = null;
        mainInst.flyTo(step.flyTo);
      }, 80);
    }
  }

  if (step.flyTo2) {
    const delay = step.flyTo2Delay ?? 2600;
    _flyTo2Timer = setTimeout(() => {
      _flyTo2Timer = null;
      mainInst.flyTo(step.flyTo2);
      if (step.flyTo2GeoLevel || step.flyTo2MapView) {
        mainInst.setHighlightsState({
          geoLevel: step.flyTo2GeoLevel,
          ...(step.flyTo2MapView || {}),
        });
      }
    }, delay);
  }
}

// ─── Style tokens ─────────────────────────────────────────────────────────────
const F = "'Inter', 'DM Sans', sans-serif";
const TEAL      = "#0d9488";
const TEAL_DARK = "#0f766e";
const TEAL_GLOW = "rgba(13,148,136,0.18)";
const ACCENT    = "#06b6d4";
const EMERALD   = "#059669";
const ROSE      = "#e11d48";
const AMBER     = "#d97706";
const INK       = "#0f172a";
const MUTED     = "#475569";
const MUTED_LIGHT = "#94a3b8";
const PAPER     = "#ffffff";
const PAPER_SOFT = "linear-gradient(160deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.98) 100%)";
const BORDER    = "rgba(13,148,136,0.1)";
const BG        = "#ffffff";

// ─── Animation helpers ────────────────────────────────────────────────────────
const FU_KF = {
  "@keyframes fuIn": {
    from: { opacity: 0, transform: "translateY(14px)" },
    to:   { opacity: 1, transform: "translateY(0)" },
  },
};
const fu = (delay) => ({
  animation: `fuIn 0.52s cubic-bezier(0.22,1,0.36,1) ${delay}s both`,
  ...FU_KF,
});
const fuStagger = (base, i) => ({
  animation: `fuIn 0.5s cubic-bezier(0.22,1,0.36,1) ${(base + i * 0.09).toFixed(2)}s both`,
  ...FU_KF,
});

function CountUp({ value, isActive, duration = 900 }) {
  const [display, setDisplay] = useState(value);
  const rafRef = useRef(null);
  useEffect(() => {
    if (!isActive) { setDisplay(value); return; }
    const m = String(value).match(/^([^0-9]*)(\d+\.?\d*)([^0-9]*)$/);
    if (!m) { setDisplay(value); return; }
    const [, prefix, numStr, suffix] = m;
    const target = parseFloat(numStr);
    const hasDecimal = numStr.includes(".");
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      const cur = target * ease;
      setDisplay(`${prefix}${hasDecimal ? cur.toFixed(1) : Math.round(cur)}${suffix}`);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [isActive, value, duration]);
  return <>{display}</>;
}

function NextStepPreview() { return null; }

function MapHint({ text }) {
  return (
    <Box sx={{
      position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)",
      zIndex: 12, display: "flex", alignItems: "center", gap: 0.8,
      px: 1.5, py: 0.75, borderRadius: "999px",
      bgcolor: "rgba(15,23,42,0.72)", backdropFilter: "blur(8px)",
      boxShadow: "0 4px 18px rgba(0,0,0,0.18)",
      whiteSpace: "nowrap", pointerEvents: "none",
      animation: "hintEntrance 0.5s cubic-bezier(0.22,1,0.36,1) 0.8s both",
      "@keyframes hintEntrance": { from: { opacity: 0, transform: "translateX(-50%) translateY(8px)" }, to: { opacity: 1, transform: "translateX(-50%) translateY(0)" } },
    }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
      </svg>
      <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.88)", fontFamily: F, fontWeight: 600, letterSpacing: "0.01em", lineHeight: 1 }}>
        {text || "Hover over any area on the map to explore"}
      </Typography>
    </Box>
  );
}

function QuestionOverlay({ overlay }) {
  if (!overlay) return null;
  return (
    <Box sx={{
      position: "absolute",
      bottom: 12,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 11,
      width: "max-content",
      maxWidth: { xs: "calc(100% - 24px)", sm: 560 },
      px: 1.75,
      py: 1.1,
      borderRadius: "12px",
      bgcolor: "rgba(255,255,255,0.95)",
      backdropFilter: "blur(12px)",
      border: "1px solid rgba(15,23,42,0.08)",
      boxShadow: "0 10px 32px rgba(15,23,42,0.12)",
      textAlign: "center",
      animation: "fuIn 0.45s ease 0.2s both",
      ...FU_KF,
    }}>
      <Typography sx={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: TEAL, fontFamily: F, mb: 0.4 }}>
        {overlay.eyebrow}
      </Typography>
      <Typography sx={{ fontSize: "12px", fontWeight: 600, color: INK, fontFamily: F, lineHeight: 1.45 }}>
        {overlay.text}
      </Typography>
    </Box>
  );
}

function GeoChangeHint({ from, to }) {
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: 1, mt: 2, px: 1.4, py: 0.85,
      borderRadius: "10px", bgcolor: "rgba(6,182,212,0.07)", border: "1px solid rgba(6,182,212,0.2)",
      animation: "fuIn 0.45s ease 0.5s both", ...FU_KF,
    }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <Typography sx={{ fontSize: "11px", color: MUTED, fontFamily: F }}>
        Map zooming from <strong style={{ color: INK }}>{from}</strong> to <strong style={{ color: TEAL }}>{to}</strong>
      </Typography>
    </Box>
  );
}

// ─── Stacked bar chart ────────────────────────────────────────────────────────
function StackedBar({ opts, label }) {
  return (
    <Box sx={{
      position: "absolute", bottom: 12, left: 12, right: 12, zIndex: 10,
      px: 1.5, pt: 1.2, pb: 1.4,
      borderRadius: "14px",
      bgcolor: "rgba(255,255,255,0.96)",
      backdropFilter: "blur(14px)",
      border: "1px solid rgba(15,23,42,0.07)",
      boxShadow: "0 8px 28px rgba(0,0,0,0.1)",
      animation: "fuIn 0.5s ease 0.4s both", ...FU_KF,
    }}>
      <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 1 }}>
        {label}
      </Typography>
      {/* Stacked bar */}
      <Box sx={{ display: "flex", height: 10, borderRadius: "6px", overflow: "hidden", mb: 1.1 }}>
        {opts.map((opt) => (
          <Box key={opt.label} sx={{ width: `${opt.pct}%`, bgcolor: opt.color, flexShrink: 0 }} />
        ))}
      </Box>
      {/* Legend */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px 12px" }}>
        {opts.map((opt) => (
          <Box key={opt.label} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: opt.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: "10px", color: MUTED, fontFamily: F, lineHeight: 1.2, whiteSpace: "nowrap" }}>
              {opt.label} <strong style={{ color: INK }}>{opt.pct}%</strong>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── Compact map legend ───────────────────────────────────────────────────────
function PmLegend() {
  return (
    <Box sx={{
      position: "absolute", top: 46, left: 12, zIndex: 10, pointerEvents: "none",
      px: 1.25, py: 0.9, borderRadius: "10px",
      bgcolor: "rgba(255,255,255,0.93)", backdropFilter: "blur(10px)",
      border: "1px solid rgba(15,23,42,0.07)", boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
      animation: "fuIn 0.4s ease 0.3s both", ...FU_KF,
    }}>
      <Typography sx={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 0.7 }}>
        Prediction market regulation
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
        {PM_OPTS.map((opt) => (
          <Box key={opt.key} sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: "2px", bgcolor: opt.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: "10px", color: MUTED, fontFamily: F, lineHeight: 1.2 }}>{opt.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── Geo level toggle ─────────────────────────────────────────────────────────
function GeoLevelToggle({ current, onChange }) {
  const levels = [
    { key: "states",       label: "States" },
    { key: "cd",           label: "Districts" },
    { key: "media_market", label: "Media Markets" },
  ];
  return (
    <Box sx={{
      position: "absolute", top: 12, right: 12, zIndex: 10,
      display: "flex", borderRadius: "10px", overflow: "hidden",
      border: `1px solid ${TEAL}28`,
      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    }}>
      {levels.map(({ key, label }) => {
        const active = current === key;
        return (
          <Box key={key} onClick={() => onChange(key)} sx={{
            px: 1.6, py: 0.75, cursor: "pointer",
            bgcolor: active ? TEAL : "rgba(255,255,255,0.94)",
            backdropFilter: "blur(10px)",
            color: active ? "#fff" : MUTED,
            fontSize: "11px", fontWeight: 700, fontFamily: F, letterSpacing: "0.03em",
            transition: "all 0.18s ease", userSelect: "none",
            "&:hover": { bgcolor: active ? TEAL_DARK : `${TEAL}0e` },
          }}>
            {label}
          </Box>
        );
      })}
    </Box>
  );
}

// ─── California filter toggle (Different / Not sure) ─────────────────────────
const CA_FILTER_OPTIONS = [
  { key: "prediction_markets__diff_gambling_pred_n", label: "Different from gambling", color: "#4CC9F0" },
  { key: "prediction_markets__same_gambling_pred_n", label: "Same as gambling",        color: "#F77F00" },
  { key: "prediction_markets__dk_pred_n",            label: "Not sure",                color: "#94a3b8" },
  { key: "prediction_markets__no_regulation_pred_n", label: "No regulation",           color: "#06D6A0" },
];

function CaFilterToggle({ current, onChange }) {
  return (
    <Box sx={{
      position: "absolute", bottom: 16, left: 16, zIndex: 10,
      display: "flex", borderRadius: "10px", overflow: "hidden",
      border: `1px solid rgba(0,0,0,0.1)`,
      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    }}>
      {CA_FILTER_OPTIONS.map(({ key, label, color }) => {
        const active = current === key;
        return (
          <Box key={key} onClick={() => onChange(key)} sx={{
            px: 1.4, py: 0.75, cursor: "pointer",
            bgcolor: active ? color : "rgba(255,255,255,0.94)",
            backdropFilter: "blur(10px)",
            color: active ? "#fff" : MUTED,
            fontSize: "11px", fontWeight: 700, fontFamily: F, letterSpacing: "0.03em",
            transition: "all 0.18s ease", userSelect: "none",
            "&:hover": { bgcolor: active ? color : "rgba(0,0,0,0.04)" },
          }}>
            {label}
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Intro geo toggle (State / CD / Media Market) ────────────────────────────
const INTRO_GEO_LEVELS = [
  { key: "states",       label: "State" },
  { key: "cd",           label: "CD" },
  { key: "media_market", label: "Media market" },
];

function IntroGeoToggle({ current, onChange }) {
  return (
    <Box sx={{
      position: "absolute", bottom: 16, left: 16, zIndex: 10,
      display: "flex", borderRadius: "10px", overflow: "hidden",
      border: `1px solid ${TEAL}28`,
      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    }}>
      {INTRO_GEO_LEVELS.map(({ key, label }) => {
        const active = current === key;
        return (
          <Box key={key} onClick={() => onChange(key)} sx={{
            px: 1.4, py: 0.75, cursor: "pointer",
            bgcolor: active ? TEAL : "rgba(255,255,255,0.94)",
            backdropFilter: "blur(10px)",
            color: active ? "#fff" : MUTED,
            fontSize: "11px", fontWeight: 700, fontFamily: F, letterSpacing: "0.03em",
            transition: "all 0.18s ease", userSelect: "none",
            "&:hover": { bgcolor: active ? TEAL_DARK : `${TEAL}0e` },
          }}>
            {label}
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Wave toggle ─────────────────────────────────────────────────────────────
const WAVE_LABELS = {
  1: "Wave 1",
  2: "Wave 2",
  3: "Wave 3 (latest)",
};

function WaveToggle({ current, onChange }) {
  return (
    <Box sx={{
      position: "absolute", top: 12, right: 12, zIndex: 10,
      display: "flex", borderRadius: "10px", overflow: "hidden",
      border: `1px solid ${TEAL}28`,
      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    }}>
      {[1, 2, 3].map((w) => {
        const active = current === w;
        return (
          <Box key={w} onClick={() => onChange(w)} sx={{
            px: 1.4, py: 0.75, cursor: "pointer",
            bgcolor: active ? TEAL : "rgba(255,255,255,0.94)",
            backdropFilter: "blur(10px)",
            color: active ? "#fff" : MUTED,
            fontSize: "11px", fontWeight: 700, fontFamily: F, letterSpacing: "0.03em",
            transition: "all 0.18s ease", userSelect: "none",
            "&:hover": { bgcolor: active ? TEAL_DARK : `${TEAL}0e` },
          }}>
            {WAVE_LABELS[w] || `Wave ${w}`}
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Mini CD bar ──────────────────────────────────────────────────────────────
function CdMiniBar({ cd }) {
  const bars = [
    { color: "#C1121F", pct: cd.ban,  key: "ban"  },
    { color: "#F77F00", pct: cd.same, key: "same" },
    { color: "#94a3b8", pct: cd.dk,   key: "dk"   },
    { color: "#4CC9F0", pct: cd.diff, key: "diff" },
    { color: "#06D6A0", pct: cd.none, key: "none" },
  ];
  return (
    <Box sx={{ display: "flex", height: 6, borderRadius: "4px", overflow: "hidden", mt: 0.8, mb: 0.5 }}>
      {bars.map((b) => (
        <Box key={b.key} sx={{ width: `${b.pct}%`, bgcolor: b.color, flexShrink: 0 }} />
      ))}
    </Box>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────
function StackLogo() {
  return (
    <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
      <img src="/static/images/logo.png" alt="District Profiles" style={{ height: 22, width: "auto", display: "block" }} />
    </Box>
  );
}

function Eyebrow({ children }) {
  return (
    <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: TEAL, fontFamily: F, mb: 1.4 }}>
      {children}
    </Typography>
  );
}

function Headline({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "20px", sm: "25px", md: "30px" }, fontWeight: 800, lineHeight: { xs: 1.22, md: 1.14 }, letterSpacing: "-0.03em", color: INK, fontFamily: F, mb: 1.8 }}>
      {children}
    </Typography>
  );
}

function IntroHeadline({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "15px", sm: "17px", md: "21px" }, fontWeight: 700, lineHeight: 1.35, letterSpacing: "-0.02em", color: INK, fontFamily: F, mb: 1.8 }}>
      {children}
    </Typography>
  );
}

function BodyCopy({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "14px", md: "15px" }, color: MUTED, lineHeight: 1.82, fontFamily: F, whiteSpace: "pre-line", letterSpacing: "-0.01em" }}>
      {children}
    </Typography>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 1.1 }}>
      {children}
    </Typography>
  );
}

function Panel({ children, sx = {} }) {
  return (
    <Box sx={{ width: "100%", maxWidth: 590, p: { xs: 2, md: 2.5 }, borderRadius: "22px", bgcolor: PAPER, backgroundImage: PAPER_SOFT, border: `1px solid ${BORDER}`, boxShadow: "0 8px 28px rgba(13,148,136,0.06)", ...sx }}>
      {children}
    </Box>
  );
}

function StepHeader({ index, total, chapter, isActive, onScrollNext, hasNext }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: "auto", pt: 3.5, opacity: isActive ? 1 : 0, transition: "opacity 0.35s ease", minHeight: 28 }}>
      <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: TEAL, fontFamily: F }}>
        {index + 1} of {total}
      </Typography>
      {chapter && (
        <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F, letterSpacing: "0.02em" }}>
          · {chapter}
        </Typography>
      )}
      {hasNext && (
        <Box
          onClick={onScrollNext}
          sx={{
            ml: "auto", display: "inline-flex", alignItems: "center", gap: 0.75,
            cursor: "pointer", px: 1.4, py: 0.75, borderRadius: "10px",
            border: `1px solid ${TEAL}30`, bgcolor: `${TEAL}08`,
            transition: "all 0.18s ease",
            "&:hover": { bgcolor: `${TEAL}14`, borderColor: `${TEAL}55` },
          }}
        >
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: TEAL, fontFamily: F, lineHeight: 1 }}>
            Next
          </Typography>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </Box>
      )}
    </Box>
  );
}

const WAVE_TABS = [
  { wave: 1, label: "Wave 1", date: "Oct 2025", color: "#64748b" },
  { wave: 2, label: "Wave 2", date: "Jan 2026", color: "#F59E0B" },
  { wave: 3, label: "Wave 3", date: "Mar 2026", color: "#C1121F" },
];

function ExploreCard({ step, isActive, nextStep, activeWave, onWaveChange }) {
  return (
    <>
      <StackLogo />
      <Eyebrow>Immigration</Eyebrow>
      <Headline>Track key metrics over time</Headline>
      <Typography sx={{ fontSize: { xs: "13px", md: "14px" }, color: MUTED, lineHeight: 1.75, fontFamily: F, mb: 1.5 }}>
        We asked whether people agree or disagree with the administration's approach to immigration across three survey waves. The map shows net approval by congressional district.
      </Typography>
      <Box sx={{ px: 1.5, py: 1.25, borderRadius: "10px", bgcolor: `${TEAL}08`, border: `1px solid ${TEAL}20`, mb: 2, display: "flex", alignItems: "flex-start", gap: 1.25 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
          <path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5"/>
        </svg>
        <Typography sx={{ fontSize: "12px", color: INK, lineHeight: 1.65, fontFamily: F }}>
          <strong style={{ color: TEAL }}>Click through the waves below</strong> to watch sentiment shift from October 2025 to March 2026. Every single district has moved more negative, with the sharpest swings in urban seats across the Northeast and Midwest.
        </Typography>
      </Box>

      {/* Wave toggle panel — guided interaction */}
      <Box sx={{ mt: 1, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, backgroundImage: PAPER_SOFT, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        {/* Instruction row */}
        <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, fontStyle: "italic" }}>
            Step through waves to see how sentiment has shifted over time.
          </Typography>
        </Box>

        {/* Wave tabs */}
        <Box sx={{ display: "flex", borderBottom: `1px solid ${BORDER}` }}>
          {WAVE_TABS.map((w) => {
            const active = activeWave === w.wave;
            return (
              <Box key={w.wave} onClick={() => onWaveChange(w.wave)} sx={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                gap: 0.5, px: 1, py: 1.25, cursor: "pointer",
                bgcolor: active ? `${w.color}10` : "transparent",
                borderBottom: active ? `2.5px solid ${w.color}` : "2.5px solid transparent",
                transition: "all 0.15s ease",
                "&:hover": { bgcolor: `${w.color}0c` },
              }}>
                <Typography sx={{ fontSize: "11px", fontWeight: active ? 700 : 500, color: active ? w.color : MUTED_LIGHT, fontFamily: F, lineHeight: 1.2, transition: "all 0.15s ease" }}>
                  {w.label}
                </Typography>
                <Typography sx={{ fontSize: "10px", color: active ? MUTED : MUTED_LIGHT, fontFamily: F, lineHeight: 1 }}>
                  {w.date}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Narrative for active wave */}
        <Box key={activeWave} sx={{ p: 2.5, animation: "fuIn 0.25s ease both", ...FU_KF }}>
          <Typography sx={{ fontSize: "12px", color: MUTED, fontFamily: F, lineHeight: 1.7 }}>
            {activeWave === 1 && "In October 2025, most of the country was still net positive on the administration's immigration approach. New York City seats, suburban New Jersey and much of California showed clear approval. The map was broadly green."}
            {activeWave === 2 && "By January 2026, 56 districts have flipped from net positive to net negative. Illinois is the hardest hit, with 9 seats turning negative and an average swing of -14 points. California (11 flips), Georgia (4), and Washington (4) follow. The national average has dropped 8.5 points, from +20 to +12."}
            {activeWave === 3 && "By March 2026, more than half of districts are now net negative on immigration. NYC seats that were net +15 in Wave 1 are now -35 or worse. This is a significant swing: every single district in the country has moved more negative, and the map shows you exactly where the shift has been sharpest."}
          </Typography>
        </Box>
      </Box>

      <NextStepPreview nextStep={nextStep} />
    </>
  );
}

const CHAPTER_NAMES = {
  "intro":               "The Tool & Methodology",
  "explore":             "Track Key Metrics",
  "light-touch":         "Filter by Demographic",
  "texas-cd":            "Target Messaging",
  "use-cases":           "Turn Insights Into Action",
};

// ─── Card components ──────────────────────────────────────────────────────────

function IntroCard({ step, nextStep, activeWave, onScrollNext }) {
  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <img src="/static/images/districtprofiles2.png" alt="District Profiles" style={{ height: 52, width: "auto" }} />
      </Box>
      <IntroHeadline>{step.headline}</IntroHeadline>
      <Box sx={{ mt: 1.5 }}>
        <BodyCopy>{step.copy}</BodyCopy>
      </Box>
      <Box sx={{ mt: 2.5 }}>
        {/* What the tool does */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {[
            { title: "Granular insights", desc: "Interactive maps and segment filters provide district and audience-level views of opinion data.", icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> },
            { title: "Tracking & trends", desc: "Time-series tools allow teams to monitor shifts in public attitudes and priorities over time.", icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg> },
            { title: "Strategic value", desc: "Brief leadership, target communications, and prepare for legislative meetings with district-level evidence.", icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20V10"/><path d="M18 20V4"/><path d="M6 20v-4"/></svg> },
          ].map((item) => (
            <Box key={item.title} sx={{ display: "flex", gap: 1.25, px: 1.4, py: 1, borderRadius: "10px", bgcolor: `${TEAL}06`, border: `1px solid ${TEAL}12`, textAlign: "left" }}>
              <Box sx={{ flexShrink: 0, mt: "2px" }}>{item.icon}</Box>
              <Box>
                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: TEAL, fontFamily: F, mb: 0.15 }}>{item.title}</Typography>
                <Typography sx={{ fontSize: "12px", color: MUTED, lineHeight: 1.55, fontFamily: F }}>{item.desc}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* MRP methodology */}
        <Box sx={{ mt: 1.5, px: 1.4, py: 1.1, borderRadius: "12px", bgcolor: `${TEAL}08`, border: `1px solid ${TEAL}1a`, textAlign: "left" }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: TEAL, fontFamily: F, mb: 0.3 }}>
            MRP methodology
          </Typography>
          <Typography sx={{ fontSize: "12px", color: MUTED, lineHeight: 1.65, fontFamily: F }}>
            Stack surveys thousands of American adults online each wave. We then apply MRP modelling: multilevel regression estimates the relationship between respondents' demographics and their answers, and poststratification applies those relationships to the known composition of each geography, producing local estimates for congressional districts, counties, states, and state legislative districts.
          </Typography>
        </Box>

        {/* Scroll CTA */}
        <Box
          onClick={onScrollNext}
          sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, cursor: "pointer", userSelect: "none", pointerEvents: "auto", "&:hover .scroll-label": { color: MUTED }, "&:hover .scroll-arrow": { borderColor: `${TEAL}88` } }}
        >
          <Box className="scroll-arrow" sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: "50%", border: `1.5px solid ${TEAL}44`, transition: "border-color 0.2s ease", animation: "scrollBounce 1.6s ease-in-out 1.5s infinite", "@keyframes scrollBounce": { "0%,100%": { transform: "translateY(0)" }, "50%": { transform: "translateY(5px)" } } }}>
            <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M2 3.5L5 6.5L8 3.5" stroke={TEAL} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </Box>
          <Typography className="scroll-label" sx={{ fontSize: "11px", fontWeight: 600, color: MUTED_LIGHT, fontFamily: F, letterSpacing: "0.05em", textTransform: "uppercase", transition: "color 0.2s ease" }}>Scroll to see the tool in action</Typography>
        </Box>
      </Box>
      <NextStepPreview nextStep={nextStep} />
    </>
  );
}

const REG_FILTERS = [
  {
    key: "prediction_markets__same_gambling_pred_n", label: "Same as gambling", color: "#F77F00", dataKey: "same",
    sublabel: "Most inclined toward the gambling-regulation frame",
    cluster: "Highest in VA-8, CO-2, NY-12 and WA-7 - liberal, highly educated urban districts. This suggests the \"same as gambling\" response is not confined to socially conservative areas, but also appears in districts where voters may be more comfortable with stronger regulation.",
  },
  {
    key: "prediction_markets__dk_pred_n", label: "Not sure", color: "#94a3b8", dataKey: "dk",
    sublabel: "No clear position yet",
    cluster: "Highest in AL-4, WV-1, OH-6 and TN-1 - mostly rural districts across Appalachia and the Deep South. High uncertainty suggests many voters have not yet formed a clear view on prediction markets, rather than necessarily being opposed.",
  },
  {
    key: "prediction_markets__diff_gambling_pred_n", label: "Different from gambling", color: "#4CC9F0", dataKey: "diff",
    sublabel: "More open to a separate regulatory category",
    cluster: "Strongest in HI-1, MD-4, NY-15, CA-34 and CA-28 - dense urban districts with large Hispanic, Black and Asian populations. These seats are less likely to say prediction markets should be treated the same as gambling, but many voters are still unsure what rules should apply.",
  },
  {
    key: "prediction_markets__no_regulation_pred_n", label: "No regulation", color: "#06D6A0", dataKey: "none",
    sublabel: "Most permissive view",
    cluster: "Concentrated in CA-34, CA-22, CA-21, TX-29 and TX-16 - districts with large Hispanic populations in California's Central Valley, greater Los Angeles, and the Texas border corridor. This points to a more permissive view in some majority-Hispanic districts, though further testing would be needed to understand whether this reflects views on prediction markets specifically or broader attitudes toward regulation.",
  },
  {
    key: "prediction_markets__banned_pred_n", label: "Ban entirely", color: "#C1121F", dataKey: "ban",
    sublabel: "Hardest opposition",
    cluster: "Highest in IA-2, IA-4, ME-2, NC-11 and PA-9 - mostly rural districts, though not uniformly the most partisan. Nationally, the ban response is relatively even at around 15-16%, suggesting hard opposition exists across geographies rather than being concentrated in one clear bloc.",
  },
];

function RegulationAppetiteCard({ step, isActive, nextStep, activeFilter, onFilterChange, nationalData }) {
  const filter = REG_FILTERS.find(f => f.key === activeFilter) || REG_FILTERS[0];
  const pct    = nationalData ? (nationalData[filter.dataKey] ?? null) : null;

  return (
    <>
      <StackLogo />
      <Eyebrow>Understand Emerging Issues</Eyebrow>
      <Headline>{step.headline}</Headline>
      {step.copy && <BodyCopy>{step.copy}</BodyCopy>}

      {/* Unified tab + stat box */}
      <Box sx={{ mt: 1.5, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, backgroundImage: PAPER_SOFT, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>

        {/* Instruction row */}
        <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, fontStyle: "italic" }}>
            Toggle each response to see where different views are concentrated on the map.
          </Typography>
        </Box>

        {/* Tab row */}
        <Box sx={{ display: "flex", borderBottom: `1px solid ${BORDER}` }}>
          {REG_FILTERS.map((f) => {
            const active = f.key === activeFilter;
            return (
              <Box key={f.key} onClick={() => onFilterChange(f.key)} sx={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                gap: 0.5, px: 0.5, py: 1, cursor: "pointer",
                bgcolor: active ? `${f.color}10` : "transparent",
                borderBottom: active ? `2.5px solid ${f.color}` : "2.5px solid transparent",
                transition: "all 0.15s ease",
                "&:hover": { bgcolor: `${f.color}0c` },
              }}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: active ? f.color : `${f.color}70`, transition: "background 0.15s ease", flexShrink: 0 }} />
                <Typography sx={{ fontSize: "10px", fontWeight: active ? 700 : 500, color: active ? f.color : MUTED_LIGHT, fontFamily: F, lineHeight: 1.2, textAlign: "center", transition: "all 0.15s ease" }}>
                  {f.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Stat + cluster */}
        <Box key={activeFilter} sx={{ p: 2.5, animation: "fuIn 0.25s ease both", ...FU_KF }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, pb: filter.cluster ? 1.5 : 0 }}>
            <Typography sx={{ fontSize: "56px", fontWeight: 800, fontFamily: F, lineHeight: 1, letterSpacing: "-0.06em", color: filter.color, flexShrink: 0 }}>
              {pct !== null ? <CountUp value={`${pct}%`} isActive={isActive} /> : "—"}
            </Typography>
            <Box>
              <Typography sx={{ fontSize: "14px", fontWeight: 700, color: INK, fontFamily: F, lineHeight: 1.2 }}>{filter.label}</Typography>
              <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, mt: 0.3, lineHeight: 1.3 }}>{filter.sublabel}</Typography>
            </Box>
          </Box>
          {filter.cluster && (
            <Typography sx={{ fontSize: "12px", color: MUTED, fontFamily: F, lineHeight: 1.7, borderTop: `1px solid ${BORDER}`, pt: 1.25 }}>
              {filter.cluster}
            </Typography>
          )}
        </Box>

      </Box>

      <NextStepPreview nextStep={nextStep} />
    </>
  );
}

const SLIDE3_METRICS = [
  { key: "better", label: "Mostly better", color: "#59a14f", statLabel: "think AI will make society better", prefKey: "model_impact_society_1_pred_n" },
  { key: "worse",  label: "Mostly worse",  color: "#e15759", statLabel: "think AI will make society worse",  prefKey: "model_impact_society_3_pred_n" },
  { key: "aidk",   label: "Don't know",    color: "#94a3b8", statLabel: "are unsure about AI's impact",     prefKey: "model_impact_society_4_pred_n" },
];

function DemoToggleCard({ step, isActive, nextStep, selectedDemoGroup, onDemoGroupChange, slide3Metric, onSlide3MetricChange }) {
  const group = DEMO_GROUPS[selectedDemoGroup] ?? DEMO_GROUPS[0];
  const metric = SLIDE3_METRICS.find(m => m.key === slide3Metric) || SLIDE3_METRICS[0];
  const metricVal = group[slide3Metric] ?? 0;
  const sortedGroups = [...DEMO_GROUPS].map((g, i) => ({ ...g, idx: i })).sort((a, b) => (b[slide3Metric] ?? 0) - (a[slide3Metric] ?? 0));
  const maxVal = Math.max(...DEMO_GROUPS.map(g => g[slide3Metric] ?? 0));

  return (
    <>
      <StackLogo />
      <Eyebrow>AI &amp; Society</Eyebrow>
      <Headline>{step.headline}</Headline>
      <Typography sx={{ fontSize: { xs: "13px", md: "14px" }, color: MUTED, lineHeight: 1.75, fontFamily: F, mb: 1.5 }}>
        We asked whether people think AI will make society mostly better or mostly worse. The map shows net optimism by geography. Nationally, the public is net negative at about -12 points, but the average hides a generational divide.
      </Typography>
      <Box sx={{ px: 1.5, py: 1.25, borderRadius: "10px", bgcolor: `${TEAL}08`, border: `1px solid ${TEAL}20`, mb: 1, display: "flex", alignItems: "flex-start", gap: 1.25 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
          <path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5"/>
        </svg>
        <Typography sx={{ fontSize: "12px", color: INK, lineHeight: 1.65, fontFamily: F }}>
          <strong style={{ color: TEAL }}>Click a group below</strong> to filter the map and see how opinion shifts. Young men are net positive (+20), while older women are deeply pessimistic (-36). Use the <strong>toggle in the top right of the map</strong> to switch between states, districts, and media markets.
        </Typography>
      </Box>

      {/* Demographic group selector */}
      <Box sx={{ width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, backgroundImage: PAPER_SOFT, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        {DEMO_GROUPS.map((g, i) => {
          const isSelected = i === selectedDemoGroup;
          const net = g.better - g.worse;
          const netColor = net > 0 ? "#2E7D32" : "#C62828";
          return (
            <Box key={g.label} onClick={() => onDemoGroupChange(i)} sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              px: 2, py: 1.25, cursor: "pointer",
              borderBottom: i < DEMO_GROUPS.length - 1 ? `1px solid ${BORDER}` : "none",
              bgcolor: isSelected ? `${TEAL}08` : "transparent",
              borderLeft: isSelected ? `3px solid ${TEAL}` : "3px solid transparent",
              transition: "all 0.15s ease",
              "&:hover": { bgcolor: isSelected ? `${TEAL}0c` : "rgba(15,23,42,0.02)" },
            }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "13px", fontWeight: isSelected ? 700 : 500, color: isSelected ? INK : MUTED, fontFamily: F, lineHeight: 1.3 }}>
                  {g.label}
                </Typography>
                <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, lineHeight: 1.3, mt: 0.15 }}>
                  {g.desc}
                </Typography>
              </Box>
              {isSelected ? (
                <Box key={`stat-${i}`} sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0, ml: 1, animation: "fuIn 0.3s ease both", ...FU_KF }}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#2E7D32", fontFamily: F, lineHeight: 1 }}>
                      {isActive ? <CountUp value={`${g.better}%`} isActive={isActive} /> : `${g.better}%`}
                    </Typography>
                    <Typography sx={{ fontSize: "8px", color: MUTED_LIGHT, fontFamily: F, mt: 0.2 }}>better</Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#C62828", fontFamily: F, lineHeight: 1 }}>
                      {isActive ? <CountUp value={`${g.worse}%`} isActive={isActive} /> : `${g.worse}%`}
                    </Typography>
                    <Typography sx={{ fontSize: "8px", color: MUTED_LIGHT, fontFamily: F, mt: 0.2 }}>worse</Typography>
                  </Box>
                  <Box sx={{ textAlign: "center", minWidth: 32 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 800, color: netColor, fontFamily: F, lineHeight: 1 }}>
                      {isActive ? <CountUp value={`${net > 0 ? "+" : ""}${net}`} isActive={isActive} /> : `${net > 0 ? "+" : ""}${net}`}
                    </Typography>
                    <Typography sx={{ fontSize: "8px", color: MUTED_LIGHT, fontFamily: F, mt: 0.2 }}>net</Typography>
                  </Box>
                </Box>
              ) : (
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: netColor, fontFamily: F, flexShrink: 0, ml: 1, opacity: 0.6 }}>
                  {net > 0 ? "+" : ""}{net}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>


      <NextStepPreview nextStep={nextStep} />
    </>
  );
}


// Seat demographic context for CA panels
const CA_SEAT_CONTEXT = {
  "prediction_markets__diff_gambling_pred_n": "Working-class, diverse districts across greater Los Angeles, with higher Hispanic and Black populations and lower college attainment than the state average. These areas show relatively stronger support for treating prediction markets differently from gambling, though further testing would be needed to understand why.",
  "prediction_markets__dk_pred_n": "Spread across urban and semi-rural districts, without one clear geographic cluster. These areas show higher uncertainty rather than clear opposition, suggesting many voters have not yet formed a settled view on how prediction markets should be regulated.",
  "prediction_markets__no_regulation_pred_n": "CA-21 and CA-22 stand out as Central Valley districts with relatively higher \"no regulation\" responses. These seats are older, whiter, and more rural than the state average, which may point to broader scepticism toward regulation rather than a prediction-market-specific view.",
};

// Approximate geographic centers for CA CD zoom
const CA_CD_CENTERS = {
  "0601": [-122.4, 40.1], "0602": [-121.6, 39.7], "0603": [-122.0, 38.3],
  "0604": [-121.5, 38.6], "0605": [-121.0, 38.4], "0606": [-120.8, 37.9],
  "0607": [-122.3, 37.9], "0608": [-122.1, 37.7], "0609": [-122.0, 37.6],
  "0610": [-121.9, 37.5], "0611": [-122.0, 37.4], "0612": [-121.9, 37.3],
  "0613": [-122.1, 37.2], "0614": [-121.7, 37.2], "0615": [-121.8, 37.4],
  "0616": [-122.0, 37.4], "0617": [-121.9, 37.3], "0618": [-120.1, 36.7],
  "0619": [-121.8, 36.6], "0620": [-119.8, 36.8], "0621": [-119.3, 36.5],
  "0622": [-119.0, 36.2], "0623": [-118.5, 35.4], "0624": [-117.6, 34.1],
  "0625": [-117.3, 34.0], "0626": [-118.2, 34.2], "0627": [-118.3, 34.1],
  "0628": [-118.5, 34.2], "0629": [-118.2, 34.0], "0630": [-118.0, 34.0],
  "0631": [-118.1, 34.1], "0632": [-118.3, 34.3], "0633": [-118.4, 34.1],
  "0634": [-118.2, 33.9], "0635": [-118.1, 33.9], "0636": [-118.0, 33.8],
  "0637": [-118.2, 33.8], "0638": [-118.4, 34.0], "0639": [-117.9, 33.9],
  "0640": [-117.7, 33.7], "0641": [-117.5, 33.7], "0642": [-117.2, 33.7],
  "0643": [-116.5, 33.9], "0644": [-117.3, 34.1], "0645": [-117.7, 33.6],
  "0646": [-117.8, 33.7], "0647": [-117.8, 33.6], "0648": [-118.5, 34.0],
  "0649": [-118.3, 34.1], "0650": [-122.4, 37.8], "0651": [-122.5, 37.9],
  "0652": [-122.3, 37.8],
};

function caTopCenter(areaIds) {
  const pts = areaIds.map(id => CA_CD_CENTERS[id]).filter(Boolean);
  if (!pts.length) return [-119.5, 37.0];
  return [
    pts.reduce((s, p) => s + p[0], 0) / pts.length,
    pts.reduce((s, p) => s + p[1], 0) / pts.length,
  ];
}

// CD config: area_id → display name + region — all 52 CA districts
const CA_CD_CONFIG = [
  { name: "CA-1",  areaId: "0601", region: "North Coast / Redding" },
  { name: "CA-2",  areaId: "0602", region: "Sacramento Valley north" },
  { name: "CA-3",  areaId: "0603", region: "Napa / Solano" },
  { name: "CA-4",  areaId: "0604", region: "Sacramento suburbs east" },
  { name: "CA-5",  areaId: "0605", region: "Sacramento" },
  { name: "CA-6",  areaId: "0606", region: "Stockton / San Joaquin" },
  { name: "CA-7",  areaId: "0607", region: "East Bay / Concord" },
  { name: "CA-8",  areaId: "0608", region: "East Bay / Oakland" },
  { name: "CA-9",  areaId: "0609", region: "East Bay / Hayward" },
  { name: "CA-10", areaId: "0610", region: "Livermore / Tri-Valley" },
  { name: "CA-11", areaId: "0611", region: "San Mateo / Fremont" },
  { name: "CA-12", areaId: "0612", region: "South Bay / Sunnyvale" },
  { name: "CA-13", areaId: "0613", region: "San Jose west" },
  { name: "CA-14", areaId: "0614", region: "San Jose east" },
  { name: "CA-15", areaId: "0615", region: "Santa Clara / Milpitas" },
  { name: "CA-16", areaId: "0616", region: "Silicon Valley / Cupertino" },
  { name: "CA-17", areaId: "0617", region: "San Jose / South Bay" },
  { name: "CA-18", areaId: "0618", region: "Fresno west" },
  { name: "CA-19", areaId: "0619", region: "Monterey / Salinas" },
  { name: "CA-20", areaId: "0620", region: "Fresno central" },
  { name: "CA-21", areaId: "0621", region: "Central Valley" },
  { name: "CA-22", areaId: "0622", region: "Tulare / Kings County" },
  { name: "CA-23", areaId: "0623", region: "Kern / Bakersfield" },
  { name: "CA-24", areaId: "0624", region: "Santa Barbara / Ventura" },
  { name: "CA-25", areaId: "0625", region: "San Fernando Valley north" },
  { name: "CA-26", areaId: "0626", region: "San Fernando Valley west" },
  { name: "CA-27", areaId: "0627", region: "Pasadena / Arcadia" },
  { name: "CA-28", areaId: "0628", region: "Hollywood / Burbank" },
  { name: "CA-29", areaId: "0629", region: "LA east / El Monte" },
  { name: "CA-30", areaId: "0630", region: "LA south / Inglewood" },
  { name: "CA-31", areaId: "0631", region: "San Bernardino west" },
  { name: "CA-32", areaId: "0632", region: "LA central" },
  { name: "CA-33", areaId: "0633", region: "LA west / Santa Monica" },
  { name: "CA-34", areaId: "0634", region: "Long Beach" },
  { name: "CA-35", areaId: "0635", region: "LA southeast" },
  { name: "CA-36", areaId: "0636", region: "Orange County north" },
  { name: "CA-37", areaId: "0637", region: "LA south / Torrance" },
  { name: "CA-38", areaId: "0638", region: "San Bernardino / Pomona" },
  { name: "CA-39", areaId: "0639", region: "Orange County / Anaheim" },
  { name: "CA-40", areaId: "0640", region: "Riverside / San Bernardino" },
  { name: "CA-41", areaId: "0641", region: "Riverside / Temecula" },
  { name: "CA-42", areaId: "0642", region: "San Diego north inland" },
  { name: "CA-43", areaId: "0643", region: "Inland Empire east" },
  { name: "CA-44", areaId: "0644", region: "San Bernardino east" },
  { name: "CA-45", areaId: "0645", region: "Orange County / Irvine" },
  { name: "CA-46", areaId: "0646", region: "Orange County / Anaheim south" },
  { name: "CA-47", areaId: "0647", region: "OC coast / Laguna Beach" },
  { name: "CA-48", areaId: "0648", region: "LA south / Compton" },
  { name: "CA-49", areaId: "0649", region: "San Diego north coast" },
  { name: "CA-50", areaId: "0650", region: "San Francisco" },
  { name: "CA-51", areaId: "0651", region: "San Diego south / National City" },
  { name: "CA-52", areaId: "0652", region: "San Diego central" },
];

function TexasCdCard({ step, nextStep, caFilter, onCaFilterChange, caCdData }) {
  const displayData = caCdData || [];

  const caOpt = CA_FILTER_OPTIONS.find(f => f.key === caFilter) || CA_FILTER_OPTIONS[0];
  const valKey = caFilter === "model_impact_society_1_pred_n" ? "better"
               : caFilter === "model_impact_society_3_pred_n" ? "worse" : "aidk";

  const topFor = (key) => [...displayData].sort((a, b) => (b[key] || 0) - (a[key] || 0)).slice(0, 2);
  const topDiff = topFor("diff");
  const topDk   = topFor("dk");
  const topNone = topFor("none");

  const panelAvg = (cds, key) => cds.length
    ? Math.round(cds.reduce((s, d) => s + (d[key] || 0), 0) / cds.length)
    : null;

  // For no-regulation, pin to CA-21/CA-22 — they are the most distinct and
  // geographically coherent pair even if not the absolute top-2 by number
  const ca21 = displayData.find(d => d.name === "CA-21");
  const ca22 = displayData.find(d => d.name === "CA-22");
  const nonePin = [ca21, ca22].filter(Boolean);
  const noneStat = nonePin.length ? Math.round(nonePin.reduce((s, d) => s + (d.none || 0), 0) / nonePin.length) : panelAvg(topNone, "none");

  const panels = [
    { filterKey: "model_impact_society_1_pred_n", color: "#59a14f", statLabel: "think AI will make society better", label: "Mostly better" },
    { filterKey: "model_impact_society_3_pred_n", color: "#e15759", statLabel: "think AI will make society worse",  label: "Mostly worse" },
    { filterKey: "model_impact_society_4_pred_n", color: "#94a3b8", statLabel: "are unsure about AI's impact",     label: "Don't know" },
  ];

  return (
    <>
      <StackLogo />
      <Eyebrow>Drill Down to District Level</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      {/* Unified tabbed box */}
      <Box sx={{ mt: 1.5, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, backgroundImage: PAPER_SOFT, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>

        {/* Tab row */}
        <Box sx={{ display: "flex", borderBottom: `1px solid ${BORDER}` }}>
          {CA_FILTER_OPTIONS.map(({ key, label, color }) => {
            const active = caFilter === key;
            return (
              <Box key={key} onClick={() => onCaFilterChange(key)} sx={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                gap: 0.5, px: 0.5, py: 1, cursor: "pointer",
                bgcolor: active ? `${color}10` : "transparent",
                borderBottom: active ? `2.5px solid ${color}` : "2.5px solid transparent",
                transition: "all 0.15s ease",
                "&:hover": { bgcolor: `${color}0c` },
              }}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: active ? color : `${color}70`, transition: "background 0.15s ease" }} />
                <Typography sx={{ fontSize: "10px", fontWeight: active ? 700 : 500, color: active ? color : MUTED_LIGHT, fontFamily: F, lineHeight: 1.2, textAlign: "center", transition: "all 0.15s ease" }}>
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Hint / content area */}
        {!caFilter ? (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, fontStyle: "italic" }}>
              Select a response above to zoom into the top California districts on the map.
            </Typography>
          </Box>
        ) : (
          <Box key={caFilter} sx={{ animation: "fuIn 0.25s ease both", ...FU_KF }}>

            {/* Top 5 bar chart */}
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
              <Typography sx={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 1 }}>
                Top 5 California districts · "{caOpt.label}"
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.65 }}>
                {[...displayData].sort((a, b) => (b[valKey] || 0) - (a[valKey] || 0)).slice(0, 5).map((cd) => {
                  const val = cd[valKey] ?? 0;
                  return (
                    <Box key={cd.name} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ minWidth: 42 }}>
                        <Typography sx={{ fontSize: "12px", fontWeight: 800, color: INK, fontFamily: F, lineHeight: 1.1 }}>{cd.name}</Typography>
                        <Typography sx={{ fontSize: "9px", color: MUTED_LIGHT, fontFamily: F, lineHeight: 1 }}>{cd.region}</Typography>
                      </Box>
                      <Box sx={{ flex: 1, height: 5, borderRadius: "3px", bgcolor: `${caOpt.color}15`, overflow: "hidden" }}>
                        <Box sx={{ width: `${val}%`, height: "100%", bgcolor: caOpt.color, borderRadius: "3px", transition: "all 0.45s cubic-bezier(0.22,1,0.36,1)" }} />
                      </Box>
                      <Typography sx={{ fontSize: "12px", fontWeight: 700, color: caOpt.color, fontFamily: F, minWidth: 30, textAlign: "right" }}>{val}%</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Seat context */}
            {CA_SEAT_CONTEXT[caFilter] && (
              <Box sx={{ mx: 2, mb: 2, px: 1.5, py: 1.1, borderRadius: "10px", bgcolor: `${TEAL}06`, border: `1px solid ${TEAL}18` }}>
                <Typography sx={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.13em", textTransform: "uppercase", color: TEAL, fontFamily: F, mb: 0.5 }}>
                  Who sits in these seats
                </Typography>
                <Typography sx={{ fontSize: "12px", color: MUTED, fontFamily: F, lineHeight: 1.65 }}>
                  {CA_SEAT_CONTEXT[caFilter]}
                </Typography>
              </Box>
            )}

          </Box>
        )}

      </Box>

      <NextStepPreview nextStep={nextStep} />
    </>
  );
}

// ─── Demographic insight callout (national DEMO_GROUPS data) ─────────────────
const DEMO_INSIGHT = {
  diff: {
    high: { label: "Men 18–34",         pct: 22 },
    low:  { label: "Women 65+",         pct: 12 },
    note: "Young men are most likely to view prediction markets as a distinct financial category.",
  },
  none: {
    high: { label: "Men 18–34",         pct: 15 },
    low:  { label: "Women 65+",         pct: 4  },
    note: "The no-regulation preference is heavily skewed to younger men, a narrow but motivated segment.",
  },
  dk: {
    high: { label: "Women 65+",         pct: 38 },
    low:  { label: "Men 18–34",         pct: 11 },
    note: "Older women are by far the most undecided group, the broadest persuadable audience.",
  },
};

function DemoInsightCallout({ metric }) {
  const insight = DEMO_INSIGHT[metric];
  if (!insight) return null;
  const gap = insight.high.pct - insight.low.pct;
  return (
    <Box sx={{ mt: 1.25, px: 1.5, py: 1.1, borderRadius: "12px", bgcolor: `${TEAL}06`, border: `1px solid ${TEAL}18`, ...fu(0.28) }}>
      <Typography sx={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.13em", textTransform: "uppercase", color: TEAL, fontFamily: F, mb: 0.6 }}>
        National demographic split
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6, mb: 0.5 }}>
        <Typography sx={{ fontSize: "20px", fontWeight: 800, color: TEAL, fontFamily: F, lineHeight: 1, letterSpacing: "-0.03em" }}>{gap}pp</Typography>
        <Typography sx={{ fontSize: "11px", color: MUTED, fontFamily: F }}>
          gap: {insight.high.label} ({insight.high.pct}%) vs {insight.low.label} ({insight.low.pct}%)
        </Typography>
      </Box>
      <Typography sx={{ fontSize: "11px", color: MUTED, fontFamily: F, lineHeight: 1.6 }}>{insight.note}</Typography>
    </Box>
  );
}

// ─── Texas Deep Dive ──────────────────────────────────────────────────────────
// Demographic context per seat cluster — driven by known TX district composition
const TX_SEAT_CONTEXT = {
  "prediction_markets__same_gambling_pred_n": "Dallas and Austin suburban seats. Higher-income, college-educated districts where voters are most likely to favour the existing gambling regulatory framework.",
  "prediction_markets__no_regulation_pred_n": "Border and South Texas districts. Majority-Hispanic working-class communities, but the anti-regulation sentiment here cuts across party lines. Trump carried several of these seats in 2024.",
  "prediction_markets__dk_pred_n": "Suburban Houston and Dallas collar seats. The most competitive districts in the state. Mixed college-educated and blue-collar electorates, with the highest share of ticket-splitters.",
  "prediction_markets__diff_gambling_pred_n": "East Texas and Rio Grande Valley districts. A mix of rural and border communities where voters are most open to a differentiated regulatory approach.",
};

function TexasDeepDiveCard({ step, nextStep, txFilter, onTxFilterChange, txCdData }) {
  const displayData = txCdData || [];

  const txOpt = CA_FILTER_OPTIONS.find(f => f.key === txFilter) || CA_FILTER_OPTIONS[1];
  const valKey = txFilter === "prediction_markets__diff_gambling_pred_n" ? "diff"
               : txFilter === "prediction_markets__dk_pred_n" ? "dk" : "none";

  const topFor = (key) => [...displayData].sort((a, b) => (b[key] || 0) - (a[key] || 0)).slice(0, 2);
  const topDiff = topFor("diff");
  const topDk   = topFor("dk");
  const topNone = topFor("none");

  const panelAvg = (cds, key) => cds.length
    ? Math.round(cds.reduce((s, d) => s + (d[key] || 0), 0) / cds.length)
    : null;

  const panels = displayData.length ? [
    { filterKey: "prediction_markets__no_regulation_pred_n", color: "#06D6A0", stat: panelAvg(topNone, "none"), statLabel: "want no regulation at all",       districts: topNone.map(d => d.name).join(" · "), region: topNone[0]?.region ?? "", desc: topNone[1]?.region ?? "" },
    { filterKey: "prediction_markets__dk_pred_n",            color: "#94a3b8", stat: panelAvg(topDk,   "dk"),   statLabel: "say 'not sure'",                   districts: topDk.map(d => d.name).join(" · "),   region: topDk[0]?.region ?? "",   desc: topDk[1]?.region ?? ""   },
    { filterKey: "prediction_markets__diff_gambling_pred_n", color: "#4CC9F0", stat: panelAvg(topDiff, "diff"), statLabel: "say 'different from gambling'",    districts: topDiff.map(d => d.name).join(" · "), region: topDiff[0]?.region ?? "", desc: topDiff[1]?.region ?? "" },
  ] : [];

  const sortedTop5 = [...displayData].sort((a, b) => (b[valKey] || 0) - (a[valKey] || 0)).slice(0, 5);

  return (
    <>
      <StackLogo />
      <Eyebrow>Prediction Markets &middot; Texas</Eyebrow>
      <Headline>{step.headline}</Headline>
      <Typography sx={{ fontSize: { xs: "13px", md: "14px" }, color: MUTED, lineHeight: 1.75, fontFamily: F, mb: 1 }}>
        We asked people how prediction markets should be regulated: banned entirely, treated the same as gambling, regulated differently, left unregulated, or not sure. District Profiles lets you zoom into any state and see how those responses break down seat by seat. Here we look at Texas's 38 congressional districts ahead of the 2027 legislative session, showing where to target lobbying, shape messaging, or brief leadership before key votes.
      </Typography>
      <Box sx={{ px: 1.5, py: 1.25, borderRadius: "10px", bgcolor: `${TEAL}08`, border: `1px solid ${TEAL}20`, mb: 1, display: "flex", alignItems: "flex-start", gap: 1.25 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
          <path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5"/>
        </svg>
        <Typography sx={{ fontSize: "12px", color: INK, lineHeight: 1.65, fontFamily: F }}>
          <strong style={{ color: TEAL }}>Click a response below</strong> to highlight the top 5 districts for that view on the map.
        </Typography>
      </Box>

      {/* Response category toggles */}
      <Box sx={{ width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, backgroundImage: PAPER_SOFT, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, fontStyle: "italic" }}>
            Select a response to see where it concentrates on the map.
          </Typography>
        </Box>
        {CA_FILTER_OPTIONS.map(({ key, label, color }, i) => {
          const active = txFilter === key;
          const context = TX_SEAT_CONTEXT[key];
          return (
            <Box key={key} onClick={() => onTxFilterChange(key)} sx={{
              px: 2, py: 1.25, cursor: "pointer",
              borderBottom: i < CA_FILTER_OPTIONS.length - 1 ? `1px solid ${BORDER}` : "none",
              bgcolor: active ? `${color}10` : "transparent",
              borderLeft: active ? `3px solid ${color}` : "3px solid transparent",
              transition: "all 0.15s ease",
              "&:hover": { bgcolor: active ? `${color}14` : "rgba(15,23,42,0.02)" },
            }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: "13px", fontWeight: active ? 700 : 500, color: active ? INK : MUTED, fontFamily: F }}>
                    {label}
                  </Typography>
                </Box>
              </Box>
              {active && (
                <Box sx={{ animation: "fuIn 0.2s ease both", ...FU_KF, pl: 2.25, mt: 0.75 }}>
                  {context && (
                    <Typography sx={{ fontSize: "11px", color: MUTED, fontFamily: F, lineHeight: 1.55 }}>
                      {context}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F, fontStyle: "italic", mt: 0.5 }}>
                    Click again to deselect
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <NextStepPreview nextStep={nextStep} />
    </>
  );
}

// ─── Use Cases ────────────────────────────────────────────────────────────────
const USE_CASES = [
  {
    color: "#4CC9F0",
    title: "Brief leadership with local evidence",
    body: "Generate district-level briefing packs for legislators showing exactly how their constituents feel about the issues that matter to your organisation.",
  },
  {
    color: "#06D6A0",
    title: "Target communications by geography",
    body: "Identify which districts need reassurance, education, or mobilisation on any issue, so messaging and ad spend are matched to local opinion rather than applied uniformly.",
  },
  {
    color: "#F77F00",
    title: "Track sentiment over time",
    body: "Monitor how attitudes shift wave over wave. Spot where support is hardening, where opposition is softening, and where the persuadable middle is largest.",
  },
  {
    color: "#8b5cf6",
    title: "Segment audiences for precision",
    body: "Filter by age, gender, education, ethnicity, and 2024 vote to understand which demographic groups are movable on your issue and tailor strategy accordingly.",
  },
];

function UseCasesCard({ step, nextStep }) {
  return (
    <>
      <StackLogo />
      <Eyebrow>What Teams Use It For</Eyebrow>
      <Headline>{step.headline}</Headline>

      <Panel sx={{ mt: 0.5 }}>
        {USE_CASES.map((uc, i) => (
          <Box key={uc.title} sx={{
            pt: i === 0 ? 0 : 1.5,
            pb: i === USE_CASES.length - 1 ? 0 : 1.5,
            borderBottom: i < USE_CASES.length - 1 ? `1px solid ${BORDER}` : "none",
            ...fuStagger(0.07, i),
          }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: INK, fontFamily: F, lineHeight: 1.3, mb: 0.4 }}>{uc.title}</Typography>
            <Typography sx={{ fontSize: "12px", color: MUTED, fontFamily: F, lineHeight: 1.65 }}>{uc.body}</Typography>
          </Box>
        ))}
      </Panel>

      <NextStepPreview nextStep={nextStep} />
    </>
  );
}

// ─── NarrativeCard ────────────────────────────────────────────────────────────
function NarrativeCard({ step, index, total, isActive, cardRef, authState, selectedDemoGroup, onDemoGroupChange, activeRegFilter, onRegFilterChange, nationalData, activeWave, caFilter, onCaFilterChange, caCdData, txFilter, onTxFilterChange, txCdData, slide3Metric, onSlide3MetricChange, onScrollNext, onWaveChange }) {
  const navigate = useNavigate();
  const nextStep = STEPS[index + 1];

  const cardSx = {
    height: { xs: "auto", sm: "calc(100vh - 47px)" },
    minHeight: { xs: "calc(80vh - 47px)", sm: "auto" },
    flexShrink: 0,
    scrollSnapAlign: "start",
    scrollSnapStop: "always",
    display: "flex",
    flexDirection: "column",
    justifyContent: step.isCta ? "center" : "flex-start",
    alignItems: step.isCta ? "center" : "flex-start",
    pt: { xs: 3.5, sm: "10vh", md: "14vh" },
    pb: { xs: 5, sm: 5, md: 7 },
    px: { xs: 2.5, sm: 3, md: 4.5 },
    position: "relative",
    overflowY: "auto",
    borderLeft: "2px solid",
    borderColor: isActive ? `${TEAL}28` : "transparent",
    opacity: isActive ? 1 : 0.38,
    transition: "opacity 0.4s ease, border-color 0.4s ease, background 0.4s ease",
    background: isActive ? `linear-gradient(90deg, ${TEAL}05 0%, transparent 20%)` : "transparent",
  };

  if (step.isCta) {
    return (
      <Box ref={cardRef} data-index={index} sx={cardSx}>
        {isActive && (
          <Box sx={{ position: { xs: "relative", md: "fixed" }, inset: { xs: "auto", md: "47px 0 0 0" }, zIndex: 20, height: { xs: "100%", md: "calc(100vh - 47px)" }, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", px: { xs: 0, md: 6 } }}>
            <Panel sx={{ maxWidth: 560, textAlign: "center", p: { xs: 3, md: 4 }, pointerEvents: "auto" }}>
              <Typography sx={{ fontSize: { xs: "28px", md: "36px" }, fontWeight: 800, letterSpacing: "-0.04em", color: INK, fontFamily: F, mb: 1.2, textAlign: "center" }}>
                {authState === "loggedIn" ? "Proceed to District Profiles" : step.headline}
              </Typography>
              {authState === "loggedIn" ? (
                <>
                  <Typography sx={{ fontSize: "15px", color: MUTED, fontFamily: F, mb: 3.2, textAlign: "center", lineHeight: 1.8 }}>
                    Explore every state, district, and demographic across a range of topics. Filter by age, gender, or education to find exactly where each message lands.
                  </Typography>
                  {/* FIX (e): mobile → /m (map), desktop → "/" (unchanged) */}
                  <Button variant="contained" disableElevation onClick={() => exitHighlights(navigate)}
                    sx={{ background: `linear-gradient(135deg, ${TEAL} 0%, #0891b2 100%)`, color: "#fff", fontFamily: F, fontWeight: 700, fontSize: "14px", textTransform: "none", borderRadius: "999px", px: 3.5, py: 1.25, boxShadow: `0 12px 26px ${TEAL_GLOW}`, "&:hover": { background: `linear-gradient(135deg, ${TEAL_DARK} 0%, #0e7490 100%)` } }}>
                    Proceed to District Profiles
                  </Button>
                </>
              ) : (
                <>
                  <Typography sx={{ fontSize: "15px", color: MUTED, fontFamily: F, mb: 3, textAlign: "center", lineHeight: 1.8 }}>
                    Sign up for a demo to explore a selection of measures across geographies and demographic groups.
                  </Typography>
                  <DemoSignupOverlay embedded />
                </>
              )}
            </Panel>
          </Box>
        )}
      </Box>
    );
  }

  const inner = (() => {
    switch (step.name) {
      case "intro":               return <IntroCard step={step} nextStep={nextStep} activeWave={activeWave} onScrollNext={onScrollNext} />;
      case "explore":             return <ExploreCard step={step} isActive={isActive} nextStep={nextStep} activeWave={activeWave} onWaveChange={onWaveChange} />;
      case "regulation-appetite": return <RegulationAppetiteCard step={step} isActive={isActive} nextStep={nextStep} activeFilter={activeRegFilter} onFilterChange={onRegFilterChange} nationalData={nationalData} />;
      case "light-touch":         return <DemoToggleCard step={step} isActive={isActive} nextStep={nextStep} selectedDemoGroup={selectedDemoGroup} onDemoGroupChange={onDemoGroupChange} slide3Metric={slide3Metric} onSlide3MetricChange={onSlide3MetricChange} />;
      case "use-cases":           return <UseCasesCard step={step} nextStep={nextStep} />;
      case "california-cd":       return <TexasCdCard step={step} nextStep={nextStep} caFilter={caFilter} onCaFilterChange={onCaFilterChange} caCdData={caCdData} />;
      case "texas-cd":            return <TexasDeepDiveCard step={step} nextStep={nextStep} txFilter={txFilter} onTxFilterChange={onTxFilterChange} txCdData={txCdData} />;
      default: return <><Headline>{step.headline}</Headline>{step.copy && <BodyCopy>{step.copy}</BodyCopy>}</>;
    }
  })();

  return (
    <Box ref={cardRef} data-index={index} sx={cardSx}>
      <Box key={isActive ? "active" : "idle"} sx={{ display: "flex", flexDirection: "column", flex: 1, width: "100%", animation: isActive ? "cardEnter 0.45s cubic-bezier(0.22,1,0.36,1) both" : "none", "@keyframes cardEnter": { from: { opacity: 0.3, transform: "translateY(10px)" }, to: { opacity: 1, transform: "translateY(0)" } } }}>
        {inner}
        <StepHeader index={index} total={total} chapter={CHAPTER_NAMES[step.name]} isActive={isActive} hasNext={!!nextStep} onScrollNext={onScrollNext} />
      </Box>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function IntroPage() {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [authState, setAuthState] = useState("guest");
  const [geoLevel, setGeoLevel] = useState("cd");
  const [selectedDemoGroup, setSelectedDemoGroup] = useState(0);
  const [activeRegFilter, setActiveRegFilter] = useState("prediction_markets__same_gambling_pred_n");
  const [nationalData, setNationalData] = useState(null);
  const [activeWave, setActiveWave] = useState(1);
  const [caFilter, setCaFilter] = useState(null);
  const [caCdData, setCaCdData] = useState(null);
  const [txFilter, setTxFilter] = useState(null);
  const [txCdData, setTxCdData] = useState(null);
  const [slide3Metric, setSlide3Metric] = useState("better");
  const [mapReady, setMapReady] = useState(false);
  const cardRefs = useRef([]);
  const mainRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const navTimerRef = useRef(null);
  const activeIndexRef = useRef(0);
  const visibleSet = useRef(new Set());              // FIX (b): mobile IO — currently-intersecting card indices
  const mapPanelRef = useRef(null);                  // FIX (c): the map panel (observed for size changes)
  const [mapFullscreen, setMapFullscreen] = useState(false); // FIX (c): xs-only fullscreen "zoom" toggle
  const [labelExpanded, setLabelExpanded] = useState(false); // FIX (e): xs-only map label pill collapsed→dot by default
  const isFinalStep = STEPS[activeIndex]?.isCta;
  const isIntroStep = activeIndex === 0;
  const activeStep = STEPS[activeIndex];

  // Map/panel split: intro gets 60/40, normal gets 48/52 (sm) or 44/56 (md)
  const mapWidth = { xs: "100%", sm: isFinalStep ? "100%" : (isIntroStep ? "40%" : "52%"), md: isFinalStep ? "100%" : (isIntroStep ? "40%" : "56%") };
  const cardMr   = { xs: 0, sm: isFinalStep ? 0 : (isIntroStep ? "40%" : "52%"), md: isFinalStep ? 0 : (isIntroStep ? "40%" : "56%") };

  // Resize Mapbox after panel width transition completes
  useEffect(() => {
    // Multiple resize calls during transition to keep map smooth
    const t1 = setTimeout(() => { getGlMap(mainRef.current)?.resize(); }, 100);
    const t2 = setTimeout(() => { getGlMap(mainRef.current)?.resize(); }, 300);
    const t3 = setTimeout(() => {
      const glMap = getGlMap(mainRef.current);
      if (glMap) {
        glMap.resize();
        const step = STEPS[activeIndex];
        if (step?.flyTo) mainRef.current?.flyTo?.(step.flyTo);
      }
    }, 500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [isIntroStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // FIX (c): shared helper — reconcile the Mapbox canvas with its CURRENT container
  // size, then RE-APPLY the current step's camera. Mapbox GL does not auto-detect CSS
  // size changes, so resize() must be called explicitly. Guards the null-map race on
  // early calls (the MainPage→MapPage ref chain isn't wired yet) by retrying ~2s.
  const resizeAndRefit = useCallback(() => {
    let tries = 0;
    const attempt = () => {
      const map = getGlMap(mainRef.current);
      if (!map) {
        if (tries++ < 20) setTimeout(attempt, 100);
        return;
      }
      map.resize();
      const step = STEPS[activeIndexRef.current];
      if (step?.flyTo) mainRef.current?.flyTo?.(step.flyTo);
    };
    attempt();
  }, []);

  // FIX (c): mobile inline framing. The short-wide 40dvh box otherwise keeps a stale
  // (taller) canvas size and fitBounds frames the US as a top-slice (→ Canada).
  // Reconcile after first paint, and re-fit on any container-size change (dvh shifts
  // as mobile browser chrome shows/hides). Desktop path untouched (early return).
  useEffect(() => {
    if (window.innerWidth >= 600) return;
    const raf = requestAnimationFrame(resizeAndRefit);
    const settle = setTimeout(resizeAndRefit, 350);
    let debounce;
    let ro;
    const el = mapPanelRef.current;
    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        clearTimeout(debounce);
        debounce = setTimeout(resizeAndRefit, 150); // coalesce chrome/transition frames
      });
      ro.observe(el);
    }
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      clearTimeout(debounce);
      if (ro) ro.disconnect();
    };
  }, [resizeAndRefit]);

  // FIX (c): mobile fullscreen "zoom" toggle. After the CSS height/top transition the
  // canvas must be resized and the step re-framed on the now-tall (or restored) box.
  useEffect(() => {
    if (window.innerWidth >= 600) return; // mobile-only
    resizeAndRefit();
    const t = setTimeout(resizeAndRefit, 350);
    return () => clearTimeout(t);
  }, [mapFullscreen, resizeAndRefit]);

  useEffect(() => {
    fetch("/api/user-info/")
      .then((r) => r.json())
      .then((d) => { if (d?.email) setAuthState("loggedIn"); else setAuthState("loggedOut"); })
      .catch(() => setAuthState("guest"));
  }, []);

  useEffect(() => {
    fetchNationalPm().then(setNationalData).catch(() => {});
  }, []);

  useEffect(() => {
    fetchCalCds().then((rows) => {
      const mapped = CA_CD_CONFIG.map((cfg) => {
        const row = findRow(rows, cfg.areaId);
        const bd = rowToBreakdown(row);
        return bd ? { name: cfg.name, areaId: cfg.areaId, region: cfg.region, ...bd } : null;
      }).filter(Boolean);
      if (mapped.length) setCaCdData(mapped);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchTexasCds().then((rows) => {
      const mapped = TX_CD_CONFIG.map((cfg) => {
        const row = findRow(rows, cfg.areaId);
        const bd = rowToBreakdown(row);
        return bd ? { name: cfg.name, areaId: cfg.areaId, region: cfg.region, ...bd } : null;
      }).filter(Boolean);
      if (mapped.length) setTxCdData(mapped);
    }).catch(() => {});
  }, []);

  const scrollToIndex = useCallback((i) => {
    const card = cardRefs.current[i];
    if (!card) return;
    const container = scrollContainerRef.current;
    if (container) container._scrollLocked = true;
    activeIndexRef.current = i;
    setActiveIndex(i);
    card.scrollIntoView({ behavior: "smooth", block: "start" });
    window.setTimeout(() => { if (container) container._scrollLocked = false; }, 900);
  }, []);

  const getNearestCardIndex = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !cardRefs.current.length) return null;

    const containerTop = container.getBoundingClientRect().top;
    let nearestIdx = null;
    let nearestDistance = Infinity;

    cardRefs.current.forEach((card, idx) => {
      if (!card) return;
      const distance = Math.abs(card.getBoundingClientRect().top - containerTop);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIdx = idx;
      }
    });

    return nearestIdx;
  }, []);

  const handleRightPanelWheel = useCallback((e) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    e.preventDefault();
    if (container._scrollLocked) return;

    const currentIdx = getNearestCardIndex();
    if (currentIdx == null) return;

    const dir = e.deltaY > 0 ? 1 : -1;
    if (dir === 0) return;

    const nextIdx = Math.max(0, Math.min(STEPS.length - 1, currentIdx + dir));
    if (nextIdx === currentIdx) return;

    scrollToIndex(nextIdx);
  }, [getNearestCardIndex, scrollToIndex]);

  // Reset geo level state when step changes
  useEffect(() => {
    const step = STEPS[activeIndex];
    if (step?.mapView?.geoLevel) setGeoLevel(step.mapView.geoLevel);
    // Reset CA filter when leaving california-cd step
    if (step?.name !== "california-cd") setCaFilter(null);
    // Reset TX filter when leaving texas-cd step
    if (step?.name !== "texas-cd") setTxFilter(null);
    // Reset slide 3 metric when leaving light-touch step
    if (step?.name !== "light-touch") setSlide3Metric("better");
    // Reset demographic group to "All adults" when entering light-touch
    if (step?.name === "light-touch") setSelectedDemoGroup(0);
    // Clear demo filters (gender/age/vote24) when entering california-cd or texas-cd
    if ((step?.name === "california-cd" || step?.name === "texas-cd") && mainRef.current) {
      mainRef.current.setHighlightsState({ gender: null, age: null, vote24: null });
    }
  }, [activeIndex]);

  const prevIndexRef = useRef(0);
  useEffect(() => {
    const prevIndex = prevIndexRef.current;
    prevIndexRef.current = activeIndex;
    activeIndexRef.current = activeIndex;
    if (navTimerRef.current) clearTimeout(navTimerRef.current);

    // Skip navigation if mapView is identical (avoids flash on intro→explore)
    // But never skip on initial mount (prevIndex === activeIndex)
    const prevMapView = STEPS[prevIndex]?.mapView;
    const nextMapView = STEPS[activeIndex]?.mapView;
    const sameMap = prevIndex !== activeIndex
      && prevMapView && nextMapView
      && prevMapView.variable === nextMapView.variable
      && prevMapView.geoLevel === nextMapView.geoLevel;

    navTimerRef.current = setTimeout(() => {
      navTimerRef.current = null;
      const stepName = STEPS[activeIndex]?.name;
      if (stepName) {
        const extra = stepName === "intro" || stepName === "explore" ? { wave: activeWave } : {};
        if (sameMap) {
          // Only fly camera, don't re-set map state
          const step = STEPS[activeIndex];
          if (step?.flyTo) mainRef.current?.flyTo?.(step.flyTo);
          mainRef.current?.setState?.({ chatHighlightAreas: step.highlights || [] });
        } else {
          navigateToStep(mainRef.current, stepName, extra);
        }
      }
    }, 160);
  }, [activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll until both metric data (mapLoading false) AND geometry (geoData set) are ready,
  // then clear the loading overlay and re-fire nav for the active step.
  useEffect(() => {
    let fired = false;
    let cdKicked = false;
    const poll = setInterval(() => {
      const inst = mainRef.current;
      if (!inst) return;
      const mapPage = inst._mapPageInstance;
      // Kick off CD load immediately, bypassing the 1.5s preload delay
      if (!cdKicked && mapPage?.loadGeo && mapPage?.state?.geoData?.features?.length > 0) {
        cdKicked = true;
        mapPage.loadGeo("cd", null, { doFit: false, backgroundOnly: true });
      }
      const loading = inst.state?.mapLoading;
      const cdLoaded = mapPage?.state?.geoPreloadStatus?.cd === "loaded";
      if (!fired && loading === false && cdLoaded) {
        const featureCount = mapPage?.state?.geoData?.features?.length ?? 0;
        // Recover a blank map. loadGeo's "stale foreground load" branch fills the
        // cache and marks geoPreloadStatus.cd as loaded, but returns WITHOUT
        // writing geoData, so the readiness gate above passes and the overlay
        // clears over an empty map. loadGeo is cache-first, so this reload is
        // cheap once the cache is warm.
        //
        // This was gated on _geoCache.has("cd:"), which NEVER matched and so
        // disabled the recovery entirely. CD cache keys carry the boundary
        // vintage and the mobile marker (MapPage builds "cd::CD120", or
        // "cd::CD120:m" on a phone), so a literal "cd:" lookup is always false.
        // Same fix as NJICreativePage, SelfDrivingPage and DataCentersPage.
        if (featureCount < 200 && mapPage) {
          mapPage.loadGeo("cd", null, { doFit: false });
        }
        fired = true;
        clearInterval(poll);
        clearTimeout(fallback);
        setMapReady(true);
        const stepName = STEPS[activeIndexRef.current]?.name;
        if (stepName) {
          const extra = (stepName === "intro" || stepName === "explore") ? { wave: activeWave } : {};
          navigateToStep(inst, stepName, extra);
        }
      }
    }, 150);
    // Fallback: unhide map after 30s regardless (e.g. very slow connection)
    const fallback = setTimeout(() => setMapReady(true), 30000);
    return () => { clearInterval(poll); clearTimeout(fallback); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Inject AI + pol variables once map is ready (separate from poll to avoid blocking)
  useEffect(() => {
    if (!mapReady) return;
    const inst = mainRef.current;
    if (!inst) return;
    try {
      const aiGroups = getAIConfig().groupsById;
      const polGroups = getPolConfig().groupsById;
      const existing = inst.state?.groupsById ?? {};
      const merged = { ...existing, ...aiGroups, immig_attitudes: polGroups.immig_attitudes };
      const aiLc = buildLabelsAndColors(aiGroups, getAIColours());
      const polLc = buildLabelsAndColors({ immig_attitudes: polGroups.immig_attitudes }, getpolColours());
      const existingLabels = inst.state?.colorLabels ?? {};
      const existingColors = inst.state?.colorMap ?? {};
      inst.setState({
        groupsById: merged,
        colorLabels: { ...existingLabels, ...aiLc.labels, ...polLc.labels },
        colorMap: { ...existingColors, ...aiLc.colors, ...polLc.colors },
      });
    } catch (e) {
      console.warn("IntroPage: failed to inject extra variables", e);
    }
  }, [mapReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update wave on map when activeWave changes while on intro slide
  useEffect(() => {
    if (STEPS[activeIndex]?.name !== "intro" && STEPS[activeIndex]?.name !== "explore") return;
    if (!mainRef.current) return;
    const step = STEPS[activeIndex];
    mainRef.current.setHighlightsState({
      variable: step?.mapView?.variable,
      geoLevel: step?.mapView?.geoLevel,
      preferenceFilter: step?.mapView?.preferenceFilter,
      wave: activeWave,
    });
  }, [activeWave]); // eslint-disable-line react-hooks/exhaustive-deps

  // Geo level toggle handler (slide 2)
  const handleRegFilterChange = useCallback((filterKey) => {
    setActiveRegFilter(filterKey);
    if (mainRef.current) {
      mainRef.current.setHighlightsState({
        variable: "prediction_markets_regulation",
        geoLevel: "cd",
        preferenceFilter: filterKey,
      });
    }
  }, []);

  const handleWaveChange = useCallback((wave) => {
    setActiveWave(wave);
    // The useEffect above handles updating the map
  }, []);

  const handleCaFilterChange = useCallback((filterKey) => {
    if (filterKey === caFilter) {
      setCaFilter(null);
      if (mainRef.current) mainRef.current.setState({ chatHighlightAreas: [] });
      const glMap = getGlMap(mainRef.current);
      if (glMap) glMap.flyTo({ center: [-119.5, 37.0], zoom: 5.5, duration: 900 });
      return;
    }
    setCaFilter(filterKey);
    const center = [-119.5, 37.0];
    if (mainRef.current) {
      mainRef.current.setHighlightsState({
        variable: "ai_impact_society",
        geoLevel: "cd",
        stateFilter: "06",
        preferenceFilter: filterKey,
      });
      mainRef.current.setState({ chatHighlightAreas: [] });
    }
    const glMap = getGlMap(mainRef.current);
    if (glMap) glMap.flyTo({ center, zoom: 5.5, duration: 900 });
  }, [caFilter, caCdData]);

  // Top 5 area IDs per filter — precomputed from API data
  const TX_TOP5 = {
    "prediction_markets__same_gambling_pred_n": ["4803", "4837", "4826", "4831", "4804"],
    "prediction_markets__diff_gambling_pred_n": ["4801", "4804", "4815", "4834", "4828"],
    "prediction_markets__dk_pred_n":            ["4804", "4801", "4806", "4802", "4805"],
    "prediction_markets__no_regulation_pred_n": ["4834", "4822", "4821", "4829", "4816"],
  };

  const handleTxFilterChange = useCallback((filterKey) => {
    if (filterKey === txFilter) {
      setTxFilter(null);
      if (mainRef.current) mainRef.current.setState({ chatHighlightAreas: [] });
      const glMap = getGlMap(mainRef.current);
      if (glMap) glMap.flyTo({ center: [-99.0, 31.5], zoom: 5.0, duration: 900 });
      return;
    }
    setTxFilter(filterKey);
    const top5Ids = TX_TOP5[filterKey] || [];
    const center = txTopCenter(top5Ids);
    if (mainRef.current) {
      mainRef.current.setHighlightsState({
        variable: "prediction_markets_regulation",
        geoLevel: "cd",
        stateFilter: "48",
        preferenceFilter: filterKey,
      });
      mainRef.current.setState({ chatHighlightAreas: top5Ids });
    }
    const glMap = getGlMap(mainRef.current);
    if (glMap) glMap.flyTo({ center, zoom: 6.2, duration: 900 });
  }, [txFilter]);

  const slide3PrefFilter = (metric) => {
    const m = SLIDE3_METRICS.find(s => s.key === metric);
    return m?.prefKey ?? "model_impact_society_1_pred_n";
  };

  const handleSlide3MetricChange = useCallback(() => {}, []);

  const handleDemoGroupChange = useCallback((i) => {
    setSelectedDemoGroup(i);
    const group = DEMO_GROUPS[i];
    if (mainRef.current) {
      mainRef.current.setHighlightsState({
        variable: "ai_impact_society",
        geoLevel: geoLevel,
        preferenceFilter: "net",
        gender: group.gender,
        age: group.age,
        vote24: group.vote24,
      });
    }
  }, [geoLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGeoLevelToggle = useCallback((level) => {
    setGeoLevel(level);
    if (mainRef.current) {
      const step = STEPS[activeIndex];
      const group = DEMO_GROUPS[selectedDemoGroup];
      mainRef.current.setHighlightsState({
        variable: step?.mapView?.variable,
        geoLevel: level,
        preferenceFilter: "net",
        gender: group?.gender,
        age: group?.age,
        vote24: group?.vote24,
      });
    }
  }, [activeIndex, selectedDemoGroup]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const container = scrollContainerRef.current;
        if (container?._scrollLocked) return;
        if (window.innerWidth < 600) {
          // FIX (b) — Mobile: the WINDOW scrolls, so the container-relative
          // getNearestCardIndex is scroll-invariant and would stick at 0. Derive the
          // active step from the IO ENTRIES instead (scroll-model-agnostic).
          entries.forEach((entry) => {
            const idx = Number(entry.target.dataset.index);
            if (entry.isIntersecting) visibleSet.current.add(idx);
            else visibleSet.current.delete(idx);
          });
          if (visibleSet.current.size > 0) setActiveIndex(Math.max(...visibleSet.current));
        } else {
          // Desktop: unchanged — the snap column is the scroller, so the
          // container-relative nearest card is correct.
          const nearestIdx = getNearestCardIndex();
          if (nearestIdx != null) setActiveIndex(nearestIdx);
        }
      },
      // FIX (b) — Mobile (< sm): observe against the viewport (root:null); the column
      // is height:auto and the window scrolls, so rooted on it the IO never fires.
      // rootMargin -40% bottom puts the "active" trigger in the top 60% of the viewport
      // (below the ~40dvh map). Desktop keeps the snap column as root; its branch
      // ignores entries, so the rootMargin/threshold are inert for it.
      { root: window.innerWidth < 600 ? null : scrollContainerRef.current, rootMargin: "0px 0px -40% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    cardRefs.current.forEach((card) => { if (card) observer.observe(card); });
    return () => observer.disconnect();
  }, [getNearestCardIndex]);

  const progress = (activeIndex / (STEPS.length - 1)) * 100;

  return (
    // FIX (a): mobile scroll trap — gate the outer wrapper so the document can grow and
    // the WINDOW scrolls on mobile (xs: auto height / visible overflow). Desktop (sm+)
    // keeps the full-height, clipped shell unchanged.
    <Box sx={{ bgcolor: BG, height: { xs: "auto", sm: "100vh" }, overflow: { xs: "visible", sm: "hidden" } }}>

      {/* Progress bar */}
      <Box role="progressbar" aria-valuenow={Math.round(progress)} sx={{ position: "fixed", top: 0, left: 0, width: "100%", height: "3px", bgcolor: `${TEAL}18`, zIndex: 1200 }}>
        <Box sx={{ height: "100%", width: `${progress}%`, background: `linear-gradient(90deg, ${TEAL} 0%, ${ACCENT} 100%)`, transition: "width 0.4s ease" }} />
      </Box>

      {/* Branding strip */}
      <Box sx={{ position: "fixed", top: 3, left: 0, right: 0, height: 44, display: "flex", alignItems: "center", px: 3, zIndex: 1100, bgcolor: "rgba(255,255,255,0.94)", backdropFilter: "blur(10px)", borderBottom: "0.5px solid rgba(15,23,42,0.07)" }}>
        <img src="/static/images/logo.png" alt="District Profiles" style={{ height: 20, width: "auto", display: "block" }} />
        <Box sx={{ ml: 1.5, px: 1, py: 0.25, borderRadius: "5px", background: `linear-gradient(135deg, ${TEAL}10 0%, ${ACCENT}10 100%)`, border: `1px solid ${TEAL}25` }}>
          <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: TEAL, fontFamily: F }}>
            Intro to District Profiles
          </Typography>
        </Box>
        {/* FIX (e): mobile → /m (map), desktop → "/" (unchanged) */}
        <Box onClick={() => exitHighlights(navigate)} sx={{ ml: "auto", width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "6px", cursor: "pointer", color: "rgba(15,23,42,0.4)", "&:hover": { bgcolor: `${TEAL}0a`, color: TEAL }, transition: "all 0.15s ease" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </Box>
      </Box>

      {/* Map panel */}
      {/* FIX (B): mobile map is FIXED (pinned) too — a top strip that stays visible and
          reframes per step while the story scrolls beneath it (the scrollytelling effect,
          mirrors PM). xs: fixed, top 47px (below the branding strip), left 0/right 0,
          full width, 40dvh. Fullscreen expands it to a 100dvh overlay (top 0, zIndex 1300).
          Desktop (sm+) is the unchanged fixed right pane. FIX (d): xs heights use dvh. */}
      <Box ref={mapPanelRef} onWheel={handleRightPanelWheel} sx={{ position: "fixed", top: { xs: mapFullscreen ? 0 : "47px", sm: "47px" }, left: { xs: 0, sm: "auto" }, right: 0, width: mapWidth, height: { xs: mapFullscreen ? "100dvh" : "40dvh", sm: "calc(100vh - 47px)" }, zIndex: { xs: mapFullscreen ? 1300 : 5, sm: 5 }, overflow: "hidden", borderLeft: { sm: isFinalStep ? "none" : "0.5px solid rgba(15,23,42,0.06)" }, bgcolor: "background.paper", transition: "width 0.45s ease, height 0.3s ease, top 0.3s ease" }}>
        <Box sx={{ height: "100%", position: "relative", overflow: "hidden" }}>
          <Box sx={{ height: "100%", filter: (isFinalStep || activeStep?.name === "use-cases") ? "blur(10px) saturate(0.8)" : "none", transform: (isFinalStep || activeStep?.name === "use-cases") ? "scale(1.04)" : "none", transition: "filter 0.5s ease, transform 0.5s ease" }}>
            <AppPanelWrapper ref={mainRef} interactive={mapFullscreen} />
          </Box>

          {isFinalStep && (
            <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(255,255,255,0.3)", backgroundImage: "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.5) 100%)", pointerEvents: "auto", zIndex: 2 }} />
          )}

          {/* Interaction blocker — blocks hover tooltips on blurred slides */}
          <Box sx={{ position: "absolute", inset: 0, zIndex: 8, cursor: "default", pointerEvents: (isFinalStep || activeStep?.name === "use-cases") ? "auto" : "none" }} />

          {/* Map loading overlay — fades out once data is ready */}
          <Box sx={{ position: "absolute", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#ffffff", opacity: mapReady ? 0 : 1, pointerEvents: mapReady ? "none" : "auto", transition: "opacity 0.6s ease" }}>
            <CircularProgress size={28} thickness={3.5} sx={{ color: TEAL }} />
          </Box>

          {/* National bar chart overlay */}
          {activeStep?.showBarChart === "pm" && (
            <StackedBar opts={PM_OPTS} label="National breakdown, prediction market regulation" />
          )}

          {/* Wave toggle (slide 1 — econ approval) */}
          {activeStep?.showWaveToggle && !isFinalStep && (
            <WaveToggle current={activeWave} onChange={handleWaveChange} />
          )}

          {/* Geo level toggle (slide 3) */}
          {activeStep?.showGeoToggle && !isFinalStep && (
            <GeoLevelToggle current={geoLevel} onChange={handleGeoLevelToggle} />
          )}

          {/* California filter toggle */}
          {activeStep?.name === "california-cd" && !isFinalStep && (
            <CaFilterToggle current={caFilter} onChange={handleCaFilterChange} />
          )}


          {/* Map hover hint — center bottom of map */}
          {activeStep?.mapHoverHint && !isFinalStep && activeStep.name !== "california-cd" && (
            <MapHint text="Hover over any district on the map to see the full breakdown" />
          )}

          {/* Immigration leaderboard — explore step */}
          {activeStep?.name === "explore" && (
            <Box sx={{
              position: "absolute", top: 12, right: 12, zIndex: 10,
              pointerEvents: "none",
              bgcolor: "rgba(255,255,255,0.93)", backdropFilter: "blur(10px)",
              border: "1px solid rgba(15,23,42,0.08)", borderRadius: "12px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              width: 240, overflow: "hidden",
              animation: "fuIn 0.4s ease 0.5s both", ...FU_KF,
            }}>
              {/* Most supportive */}
              <Box sx={{ px: 1.5, pt: 1.25, pb: 1 }}>
                <Typography sx={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#2E7D32", fontFamily: F, mb: 0.75 }}>
                  Most supportive
                </Typography>
                {(activeWave === 1
                  ? [{ d: "AL-4", net: "+46.8" }, { d: "WY-AL", net: "+45.7" }, { d: "PA-13", net: "+45.5" }]
                  : activeWave === 2
                  ? [{ d: "AL-4", net: "+40.1" }, { d: "TN-1", net: "+38.3" }, { d: "WY-AL", net: "+37.3" }]
                  : [{ d: "WY-AL", net: "+39.4" }, { d: "NE-3", net: "+37.3" }, { d: "ID-1", net: "+34.9" }]
                ).map((r) => (
                  <Box key={r.d} sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 600, color: INK, fontFamily: F }}>{r.d}</Typography>
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#2E7D32", fontFamily: F }}>{r.net}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ height: "1px", bgcolor: "rgba(15,23,42,0.06)" }} />
              {/* Most opposed */}
              <Box sx={{ px: 1.5, pt: 1, pb: 1.25 }}>
                <Typography sx={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#C62828", fontFamily: F, mb: 0.75 }}>
                  Most opposed
                </Typography>
                {(activeWave === 1
                  ? [{ d: "WA-7", net: "-17.0" }, { d: "CA-12", net: "-10.6" }, { d: "CA-2", net: "-7.9" }]
                  : activeWave === 2
                  ? [{ d: "WA-7", net: "-29.6" }, { d: "CA-11", net: "-22.8" }, { d: "CA-12", net: "-22.6" }]
                  : [{ d: "WA-7", net: "-52.3" }, { d: "CA-11", net: "-50.5" }, { d: "DC-AL", net: "-48.8" }]
                ).map((r) => (
                  <Box key={r.d} sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 600, color: INK, fontFamily: F }}>{r.d}</Typography>
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#C62828", fontFamily: F }}>{r.net}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Texas top 5 leaderboard — texas-cd step */}
          {activeStep?.name === "texas-cd" && txFilter && (
            <Box key={txFilter} sx={{
              position: "absolute", top: 12, right: 12, zIndex: 10,
              pointerEvents: "none",
              bgcolor: "rgba(255,255,255,0.93)", backdropFilter: "blur(10px)",
              border: "1px solid rgba(15,23,42,0.08)", borderRadius: "12px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              width: 220, overflow: "hidden",
              animation: "fuIn 0.4s ease both", ...FU_KF,
            }}>
              <Box sx={{ px: 1.5, py: 1.25 }}>
                <Typography sx={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: (CA_FILTER_OPTIONS.find(f => f.key === txFilter)?.color || TEAL), fontFamily: F, mb: 0.75 }}>
                  Top 5 · {CA_FILTER_OPTIONS.find(f => f.key === txFilter)?.label || ""}
                </Typography>
                {(txFilter === "prediction_markets__same_gambling_pred_n"
                  ? [{ d: "TX-3", v: "37.7%" }, { d: "TX-37", v: "37.4%" }, { d: "TX-26", v: "36.7%" }, { d: "TX-31", v: "35.9%" }, { d: "TX-4", v: "35.8%" }]
                  : txFilter === "prediction_markets__diff_gambling_pred_n"
                  ? [{ d: "TX-1", v: "24.6%" }, { d: "TX-4", v: "22.9%" }, { d: "TX-15", v: "22.6%" }, { d: "TX-34", v: "22.5%" }, { d: "TX-28", v: "22.1%" }]
                  : txFilter === "prediction_markets__dk_pred_n"
                  ? [{ d: "TX-4", v: "29.3%" }, { d: "TX-1", v: "29.3%" }, { d: "TX-6", v: "29.1%" }, { d: "TX-2", v: "29.0%" }, { d: "TX-5", v: "28.8%" }]
                  : [{ d: "TX-34", v: "12.7%" }, { d: "TX-22", v: "12.5%" }, { d: "TX-21", v: "12.4%" }, { d: "TX-29", v: "12.4%" }, { d: "TX-16", v: "12.3%" }]
                ).map((r) => (
                  <Box key={r.d} sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 600, color: INK, fontFamily: F }}>{r.d}</Typography>
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: (CA_FILTER_OPTIONS.find(f => f.key === txFilter)?.color || TEAL), fontFamily: F }}>{r.v}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Report overlay — use-cases step */}
          {activeStep?.name === "use-cases" && (
            <Box sx={{
              position: "absolute", inset: 0, zIndex: 10,
              display: "flex", alignItems: "center", justifyContent: "center",
              pointerEvents: "none",
            }}>
              {/* Background report (example) — offset behind */}
              <Box sx={{
                position: "absolute",
                transform: "rotate(-4deg) translate(-30px, 20px)",
                width: "55%", maxWidth: 340,
                borderRadius: "12px", overflow: "hidden",
                boxShadow: "0 16px 48px rgba(0,0,0,0.18)",
                opacity: 0.85,
                animation: "reportIn 0.5s cubic-bezier(0.22,1,0.36,1) 0.2s both",
                "@keyframes reportIn": {
                  from: { opacity: 0, transform: "rotate(-4deg) translate(-30px, 20px) translateY(20px) scale(0.95)" },
                  to: { opacity: 0.85, transform: "rotate(-4deg) translate(-30px, 20px) scale(1)" },
                },
              }}>
                <Box component="img" src="/static/images/examplereport.png" alt="Example report" sx={{ display: "block", width: "100%", height: "auto" }} />
              </Box>
              {/* Foreground report (political) — centered */}
              <Box sx={{
                position: "relative",
                width: "55%", maxWidth: 340,
                borderRadius: "12px", overflow: "hidden",
                boxShadow: "0 20px 56px rgba(0,0,0,0.22), 0 4px 16px rgba(0,0,0,0.1)",
                animation: "reportIn2 0.5s cubic-bezier(0.22,1,0.36,1) 0.4s both",
                "@keyframes reportIn2": {
                  from: { opacity: 0, transform: "translateY(18px) scale(0.97)" },
                  to: { opacity: 1, transform: "translateY(0) scale(1)" },
                },
              }}>
                <Box component="img" src="/static/images/polreport.png" alt="Political report" sx={{ display: "block", width: "100%", height: "auto" }} />
              </Box>
            </Box>
          )}

          {/* PM color legend */}
          {activeStep?.showLegend && !isFinalStep && <PmLegend />}

          {activeStep?.questionOverlay && !isFinalStep && (
            <QuestionOverlay overlay={activeStep.questionOverlay} />
          )}

          {/* Map label */}
          {(() => {
            const activeFilter = activeStep?.showRegFilter
              ? REG_FILTERS.find(f => f.key === activeRegFilter)
              : null;
            const activeDemoGroup = activeStep?.showDemoToggle
              ? DEMO_GROUPS[selectedDemoGroup]
              : null;
            const activeSlide3Metric = activeStep?.showDemoToggle
              ? SLIDE3_METRICS.find(m => m.key === slide3Metric)
              : null;
            const activeCaOpt = activeStep?.name === "california-cd"
              ? CA_FILTER_OPTIONS.find(f => f.key === caFilter)
              : null;
            const activeTxOpt = activeStep?.name === "texas-cd"
              ? CA_FILTER_OPTIONS.find(f => f.key === txFilter)
              : null;
            const dotColor = activeFilter ? activeFilter.color : activeDemoGroup ? (activeSlide3Metric?.color ?? "#4CC9F0") : activeCaOpt ? activeCaOpt.color : activeTxOpt ? activeTxOpt.color : null;
            const label = activeFilter
              ? `Prediction market regulation · ${activeFilter.label}`
              : activeDemoGroup
              ? `AI impact on society · ${activeSlide3Metric?.label ?? "Mostly better"} · ${activeDemoGroup.label}`
              : activeCaOpt
              ? `AI impact on society · ${activeCaOpt.label}, California CDs`
              : activeTxOpt
              ? `AI impact on society · ${activeTxOpt.label}, Texas CDs`
              : STEPS[activeIndex]?.mapLabel;
            // FIX (e): MOBILE — collapsed to just the dot by default; tap to expand the
            // full label, tap again to collapse (declutters the 40dvh map). xs drops to
            // top:54 to clear any top-row toggle when expanded. Desktop (sm): always full,
            // non-interactive (pointerEvents:none), top:12 — unchanged.
            const pillMuted = isFinalStep || activeStep?.name === "use-cases";
            return (
              <Box sx={{ position: "absolute", top: { xs: 54, sm: 12 }, left: 12, zIndex: 10, pointerEvents: { xs: pillMuted ? "none" : "auto", sm: "none" }, opacity: pillMuted ? 0 : 1, transition: "opacity 0.25s ease" }}>
                <Box
                  key={activeFilter?.key ?? activeDemoGroup?.label ?? activeCaOpt?.key ?? activeIndex}
                  onClick={() => setLabelExpanded((v) => !v)}
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.65, borderRadius: "8px", bgcolor: "rgba(255,255,255,0.93)", backdropFilter: "blur(10px)", border: `1px solid ${TEAL}20`, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", cursor: { xs: "pointer", sm: "default" }, WebkitTapHighlightColor: "transparent" }}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: dotColor ? dotColor : `linear-gradient(135deg, ${TEAL} 0%, ${ACCENT} 100%)`, flexShrink: 0 }} />
                  <Typography sx={{ display: { xs: labelExpanded ? "inline" : "none", sm: "inline" }, fontSize: "11px", fontWeight: 600, color: INK, fontFamily: F, letterSpacing: "0.01em", lineHeight: 1, whiteSpace: "nowrap" }}>
                    {label}
                  </Typography>
                </Box>
              </Box>
            );
          })()}

          {/* Intro geo toggle */}
          {activeStep?.showIntroGeoToggle && !isFinalStep && (
            <IntroGeoToggle current={geoLevel} onChange={handleGeoLevelToggle} />
          )}

          {/* FIX (c): mobile-only fullscreen "zoom" toggle — expands the map to full
              screen (interactive) for a closer per-step look; collapse restores the
              inline non-interactive strip. Scroll position + active step are preserved
              (pure CSS + resizeAndRefit). Never shown on desktop. */}
          <Box
            onClick={() => setMapFullscreen((f) => !f)}
            sx={{ display: { xs: "flex", sm: "none" }, position: "absolute", bottom: 12, right: 12, zIndex: 20, width: 36, height: 36, borderRadius: "10px", bgcolor: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)", border: `1px solid ${TEAL}33`, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", alignItems: "center", justifyContent: "center", cursor: "pointer", userSelect: "none", WebkitTapHighlightColor: "transparent" }}
          >
            {mapFullscreen ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
            )}
          </Box>
        </Box>
      </Box>

      {/* Dot nav — desktop left rail */}
      <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "center", gap: 1, position: "fixed", left: 14, top: "50%", transform: "translateY(-50%)", zIndex: 1050, opacity: isFinalStep ? 0 : 1, transition: "opacity 0.4s ease", pointerEvents: isFinalStep ? "none" : "auto" }}>
        {STEPS.filter((s) => !s.isCta).map((s, i) => (
          <Box key={i} onClick={() => scrollToIndex(i)} title={s.headline?.slice(0, 48)} sx={{ width: activeIndex === i ? 8 : 5, height: activeIndex === i ? 8 : 5, borderRadius: "50%", bgcolor: activeIndex === i ? TEAL : `${TEAL}30`, transition: "all 0.25s ease", cursor: "pointer", "&:hover": { bgcolor: TEAL, transform: "scale(1.35)" } }} />
        ))}
      </Box>

      {/* Scroll hint — desktop bottom of left panel */}
      <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "center", gap: 0.5, position: "fixed", bottom: 0, left: 0, width: { sm: "48%", md: "44%" }, pb: 2, pt: 5, background: "linear-gradient(to bottom, transparent, rgba(250,250,252,0.96) 55%)", zIndex: 1040, pointerEvents: "none", opacity: activeIndex === 1 ? 1 : 0, transition: "opacity 0.5s ease" }}>
        <Box sx={{ animation: "nudgeDown 1.6s ease-in-out infinite", "@keyframes nudgeDown": { "0%,100%": { transform: "translateY(0)", opacity: 0.5 }, "50%": { transform: "translateY(5px)", opacity: 1 } } }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </Box>
        <Typography sx={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: TEAL, fontFamily: F, opacity: 0.7 }}>Scroll</Typography>
      </Box>

      {/* FIX (e): mobile scroll hint — mobile lacks the desktop left-rail nudge, so users
          don't realise the story scrolls. Animated nudge-down + "Scroll" pill just above
          the bottom nav, on the FIRST step only (fades once they scroll on). Mobile-only. */}
      {activeIndex === 0 && !isFinalStep && (
        <Box sx={{ display: { xs: "flex", sm: "none" }, position: "fixed", bottom: "calc(34px + env(safe-area-inset-bottom) + 10px)", left: "50%", transform: "translateX(-50%)", zIndex: 1040, alignItems: "center", gap: 0.75, px: 1.75, py: 0.6, borderRadius: "999px", bgcolor: "rgba(255,255,255,0.96)", backdropFilter: "blur(8px)", border: `1px solid ${TEAL}22`, boxShadow: "0 2px 12px rgba(0,0,0,0.10)", pointerEvents: "none" }}>
          <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: TEAL, fontFamily: F }}>Scroll</Typography>
          <Box sx={{ display: "flex", animation: "nudgeDown 1.6s ease-in-out infinite", "@keyframes nudgeDown": { "0%,100%": { transform: "translateY(0)", opacity: 0.6 }, "50%": { transform: "translateY(3px)", opacity: 1 } } }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </Box>
        </Box>
      )}

      {/* Mobile bottom nav */}
      <Box sx={{ display: { xs: "flex", sm: "none" }, position: "fixed", bottom: 0, left: 0, right: 0, height: 34, bgcolor: "rgba(255,255,255,0.92)", backdropFilter: "blur(10px)", borderTop: "0.5px solid rgba(15,23,42,0.06)", zIndex: 1050, alignItems: "center", justifyContent: "space-between", px: 2 }}>
        <Box onClick={() => scrollToIndex(Math.max(0, activeIndex - 1))} sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: activeIndex === 0 ? "default" : "pointer", opacity: activeIndex === 0 ? 0.25 : 1 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
          <Typography sx={{ fontSize: "11px", fontWeight: 600, color: TEAL, fontFamily: F }}>Prev</Typography>
        </Box>
        <Typography sx={{ fontSize: "10px", fontWeight: 600, color: MUTED, fontFamily: F, letterSpacing: "0.05em" }}>{activeIndex + 1} / {STEPS.length}</Typography>
        <Box onClick={() => scrollToIndex(Math.min(STEPS.length - 1, activeIndex + 1))} sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: activeIndex === STEPS.length - 1 ? "default" : "pointer", opacity: activeIndex === STEPS.length - 1 ? 0.25 : 1 }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 600, color: TEAL, fontFamily: F }}>Next</Typography>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </Box>
      </Box>

      {/* Narrative scroll column */}
      {/* FIX (B): mobile story starts BELOW the fixed branding (47px) + pinned map (40dvh)
          via pt, and the WINDOW scrolls it (overflowY:visible, height:auto) beneath the
          pinned map. pb clears the fixed bottom nav. Desktop (sm) is the unchanged snap
          column (pt:47px, own scroll, y-mandatory snap). */}
      <Box ref={scrollContainerRef} sx={{
        mr: cardMr,
        pt: { xs: "calc(47px + 40dvh)", sm: "47px" },
        pb: { xs: "54px", sm: 0 },
        height: { xs: "auto", sm: "100vh" },
        overflowY: { xs: "visible", sm: "scroll" },
        scrollSnapType: { xs: "none", sm: "y mandatory" },
        "&::-webkit-scrollbar": { display: "none" },
        msOverflowStyle: "none",
        scrollbarWidth: "none",
        position: "relative",
        zIndex: "auto",
        transition: "margin-right 0.45s ease",
      }}>
        {STEPS.map((step, i) => (
          <NarrativeCard
            key={step.name}
            step={step}
            index={i}
            total={STEPS.length}
            isActive={activeIndex === i}
            cardRef={(el) => { cardRefs.current[i] = el; }}
            authState={authState}
            selectedDemoGroup={selectedDemoGroup}
            onDemoGroupChange={handleDemoGroupChange}
            activeRegFilter={activeRegFilter}
            onRegFilterChange={handleRegFilterChange}
            nationalData={nationalData}
            activeWave={activeWave}
            caFilter={caFilter}
            onCaFilterChange={handleCaFilterChange}
            caCdData={caCdData}
            txFilter={txFilter}
            onTxFilterChange={handleTxFilterChange}
            txCdData={txCdData}
            slide3Metric={slide3Metric}
            onSlide3MetricChange={handleSlide3MetricChange}
            onScrollNext={() => scrollToIndex(i + 1)}
            onWaveChange={handleWaveChange}
          />
        ))}
      </Box>
    </Box>
  );
}
